import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  Profile,
  Job,
  Quote,
  Message,
  Notification,
  Review,
  UserRole,
  VerificationStatus,
} from '../types';
import {
  INITIAL_PROFILES,
  INITIAL_JOBS,
  INITIAL_QUOTES,
  INITIAL_MESSAGES,
  INITIAL_NOTIFICATIONS,
  INITIAL_REVIEWS,
} from '../data/initialData';
import {
  supabase,
  checkSupabaseHealth,
  SupabaseHealthStatus,
  signUpCustomerAccount,
  signUpProfessionalAccount,
  upgradeCustomerToProfessionalAccount,
  loginWithSupabase,
  signInAdmin,
  signOutAdmin,
  fetchProfileForAuthUser,
  getSupabaseJobs,
  getSupabaseQuotes,
  getSupabaseMessages,
  getSupabaseProfiles,
  cleanContactPhone,
  isFakeAccountProfile,
  markSupabaseMessagesRead,
  createSupabaseJob,
  submitSupabaseQuote,
  acceptSupabaseQuote,
  verifyMarketplacePayment,
  sendSupabaseMessage,
  fetchProfessionalSubscription,
  updateVerifiedProfilePhoto as updateVerifiedProfilePhotoDb,
  getSupabaseAuthErrorMessage,
  notifyLoginActivityApi,
  notifyProfileUpdatedApi,
  signInWithGoogleOAuth,
  getStoredGoogleOAuthIntent,
  clearStoredGoogleOAuthIntent,
  saveGoogleOAuthIntent,
  sendTwoFactorCodeApi,
} from '../lib/supabaseService';
import {
  getAssignedProfessionalId,
  isJobForProfessionalTrade,
  runJobMatchingEngine,
} from '../lib/matchingEngine';

export type AppView =
  | 'home'
  | 'customer_auth'
  | 'pro_auth'
  | 'admin_auth'
  | 'password_reset'
  | 'customer_dashboard'
  | 'pro_dashboard'
  | 'admin_dashboard'
  | 'post_job'
  | 'available_jobs'
  | 'quote_comparison'
  | 'pro_profile'
  | 'messages'
  | 'notifications'
  | 'pro_subscription'
  | 'find_pro'
  | 'how_it_works'
  | 'become_pro'
  | 'customer_guide'
  | 'verification'
  | 'faq'
  | 'about'
  | 'contact'
  | 'privacy'
  | 'terms'
  | 'job_details'
  | 'not_found';

export interface ViewParams {
  jobId?: string;
  proId?: string;
  targetProfessionalId?: string;
  selectedProfessionalId?: string;
  targetProfessionalName?: string;
  selectedProfessionalName?: string;
  targetProfessionalBusiness?: string;
  targetProfessionalAvatar?: string;
  selectedCategory?: string;
  selectedSubcategory?: string;
  selectedState?: string;
  selectedLga?: string;
  searchQuery?: string;
  highlightQuoteId?: string;
  recipientId?: string;
  contactId?: string;
  conversationId?: string;
  category?: string;
  authMode?: 'login' | 'register';
  paystackRef?: string;
  paymentStatus?: string;
  subscriptionVerified?: boolean;
  adminTab?: string;
  tab?: string;
  activeTab?: string;
  phone?: string;
  email?: string;
  role?: UserRole;
  ref?: string;
  quoteId?: string;
  pay?: string;
  returnView?: AppView;
}

export interface PendingGoogleAuthState {
  status:
    | 'requires_onboarding'
    | 'requires_role_selection'
    | 'requires_phone_verification'
    | 'requires_profile_completion'
    | 'requires_account_linking'
    | 'requires_device_verification'
    | 'professional_approval_pending';
  requestedRole?: 'customer' | 'professional' | null;
  authMode?: 'login' | 'register';
  challengeToken?: string;
  googleUser: {
    id: string;
    email: string;
    fullName: string;
    avatarUrl?: string;
    emailVerified: boolean;
    phoneVerified: boolean;
  };
  existingAccount?: {
    existingRole: 'customer' | 'professional';
    maskedEmail: string;
    maskedPhone?: string | null;
  };
  existingProfile?: Profile;
  message?: string;
}

export interface GoogleAuthNotice {
  type:
    | 'success'
    | 'cancelled'
    | 'error'
    | 'phone_required'
    | 'profile_required'
    | 'approval_pending'
    | 'linking_required'
    | 'device_verification_required';
  title: string;
  message: string;
}

interface AppContextType {
  currentView: AppView;
  viewParams: ViewParams;
  currentUser: Profile | null;
  isAuthenticated: boolean;
  authLoading: boolean;
  profiles: Profile[];
  jobs: Job[];
  quotes: Quote[];
  messages: Message[];
  notifications: Notification[];
  reviews: Review[];
  unreadNotifsCount: number;
  supabaseHealth: SupabaseHealthStatus;
  showMigrationModal: boolean;
  setShowMigrationModal: (show: boolean) => void;
  checkHealth: () => Promise<void>;
  // Navigation & Modals
  navigateTo: (view: AppView, params?: ViewParams) => void;
  authModalState: { isOpen: boolean; mode: 'login' | 'register' };
  openAuthModal: (mode?: 'login' | 'register') => void;
  closeAuthModal: () => void;
  // Auth & Roles
  setCurrentUser: (user: Profile | null) => void;
  switchUserRole: (role: UserRole) => void;
  registerCustomer: (data: Partial<Profile> & { googleUserId?: string }, password?: string) => Promise<Profile>;
  registerProfessional: (data: Partial<Profile>, password?: string) => Promise<Profile>;
  upgradeCustomerToProfessional: (data: Partial<Profile>) => Promise<Profile>;
  updateCustomerProfile: (data: Partial<Profile>) => Promise<Profile>;
  updateProfessionalProfile: (data: Partial<Profile>) => Promise<Profile>;
  loginUser: (email: string, role: UserRole, password?: string, verified2faResult?: any) => Promise<boolean>;
  logoutUser: () => Promise<void>;
  adminLogout: () => Promise<void>;
  // Google OAuth
  pendingGoogleAuth: PendingGoogleAuthState | null;
  setPendingGoogleAuth: React.Dispatch<React.SetStateAction<PendingGoogleAuthState | null>>;
  googleAuthNotice: GoogleAuthNotice | null;
  setGoogleAuthNotice: React.Dispatch<React.SetStateAction<GoogleAuthNotice | null>>;
  initiateGoogleSignIn: (options: {
    role?: 'customer' | 'professional';
    authMode: 'login' | 'register';
    returnView?: string;
  }) => Promise<void>;
  selectRoleForGoogleUser: (role: 'customer' | 'professional') => void;
  switchActiveRole?: (role: 'customer' | 'professional') => void;
  // Job & Marketplace Actions
  postJob: (
    jobData: Omit<Job, 'id' | 'createdAt' | 'updatedAt' | 'quotesCount' | 'status'>,
    mediaFiles?: File[],
    videoFiles?: File[]
  ) => Promise<Job>;
  submitQuote: (quoteData: Omit<Quote, 'id' | 'createdAt' | 'status'>) => Promise<Quote>;
  selectProfessionalAndAcceptQuote: (
    quoteId: string,
    options?: { skipNavigate?: boolean; alreadyPaid?: boolean; paymentReference?: string }
  ) => Promise<void>;
  sendMessage: (
    jobId: string | null | undefined,
    receiverId: string,
    content: string,
    attachments?: string[],
    conversationId?: string
  ) => Promise<void>;
  fetchMessages: (
    jobId?: string | null,
    options?: { conversationId?: string; withUserId?: string }
  ) => Promise<Message[]>;
  markMessagesRead: (
    jobId: string | null | undefined,
    userId: string,
    options?: { conversationId?: string; senderId?: string }
  ) => Promise<void>;
  onlineUserIds: string[];
  markNotificationAsRead: (notificationId: string) => void;
  markAllNotificationsAsRead: () => void;
  refreshSubscriptionStatus: (proId?: string) => Promise<void>;
  refreshJobsAndQuotes: () => Promise<void>;
  // Admin & Verification Actions
  updateProfessionalVerification: (proId: string, status: VerificationStatus) => void;
  updateVerifiedProfilePhoto: (proId: string, photoUrl: string) => Promise<boolean>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

const FRESH_DB_VERSION = 'jobmatch_db_fresh_v3';
try {
  if (typeof window !== 'undefined' && window.localStorage) {
    if (window.localStorage.getItem('jobmatch_db_version') !== FRESH_DB_VERSION) {
      window.localStorage.removeItem('jobmatch_jobs');
      window.localStorage.removeItem('jobmatch_quotes');
      window.localStorage.removeItem('jobmatch_messages');
      window.localStorage.removeItem('jobmatch_notifications');
      window.localStorage.setItem('jobmatch_db_version', FRESH_DB_VERSION);
    }
  }
} catch (_e) {}

function safeGetStorage<T>(key: string, fallback: T): T {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return fallback;
    const item = window.localStorage.getItem(key);
    if (!item) return fallback;
    const parsed = JSON.parse(item);
    if (Array.isArray(fallback) && !Array.isArray(parsed)) {
      return fallback;
    }
    if (key === 'jobmatch_profiles' && Array.isArray(parsed) && parsed.length === 0) {
      return fallback;
    }
    return parsed as T;
  } catch (_e) {
    return fallback;
  }
}

function safeSetStorage(key: string, value: any): void {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      if (value === null || value === undefined) {
        window.localStorage.removeItem(key);
      } else {
        window.localStorage.setItem(key, JSON.stringify(value));
      }
    }
  } catch (_e) {
    // Gracefully ignore storage quota or access restriction errors
  }
}

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Supabase status
  const [supabaseHealth, setSupabaseHealth] = useState<SupabaseHealthStatus>({
    connected: true,
    tablesReady: false,
  });
  const [showMigrationModal, setShowMigrationModal] = useState<boolean>(false);

  // Authentication State
  const [authLoading, setAuthLoading] = useState<boolean>(true);

  // Load registered/initial profiles (stripping any legacy fake/seed accounts)
  const [profiles, setProfiles] = useState<Profile[]>(() =>
    safeGetStorage<Profile[]>('jobmatch_profiles', INITIAL_PROFILES).filter(
      (p) => !isFakeAccountProfile(p)
    )
  );

  // Current authenticated user (null when logged out)
  const [currentUser, setCurrentUser] = useState<Profile | null>(() => {
    const saved = safeGetStorage<Profile | null>('jobmatch_current_user', null);
    // Security: Never initialize with admin role or fake demo user from localStorage
    if (saved && (saved.role === 'admin' || isFakeAccountProfile(saved))) {
      return null;
    }
    return saved && saved.id && saved.role ? saved : null;
  });

  const isAuthenticated = Boolean(currentUser && currentUser.id);

  // Google OAuth onboarding / verification / linking state
  const [pendingGoogleAuth, setPendingGoogleAuth] = useState<PendingGoogleAuthState | null>(() =>
    safeGetStorage<PendingGoogleAuthState | null>('jobmatch_pending_google_auth', null)
  );
  const [googleAuthNotice, setGoogleAuthNotice] = useState<GoogleAuthNotice | null>(null);

  useEffect(() => {
    safeSetStorage('jobmatch_pending_google_auth', pendingGoogleAuth);
  }, [pendingGoogleAuth]);

  // Persist current user session to localStorage whenever currentUser updates
  useEffect(() => {
    safeSetStorage('jobmatch_current_user', currentUser);
  }, [currentUser]);

  const [jobs, setJobs] = useState<Job[]>(() =>
    safeGetStorage<Job[]>('jobmatch_jobs', INITIAL_JOBS)
  );

  useEffect(() => {
    safeSetStorage('jobmatch_jobs', jobs);
  }, [jobs]);

  const mergeLiveJobs = useCallback((liveJobs: Job[] | null | undefined, prevJobs: Job[]): Job[] => {
    if (!Array.isArray(liveJobs) || liveJobs.length === 0) {
      return prevJobs;
    }
    const map = new Map<string, Job>();
    liveJobs.forEach((j) => {
      if (j?.id) map.set(j.id, j);
    });
    // Preserve any newly created in-session jobs from prev that may not have completed async database propagation yet
    prevJobs.forEach((pj) => {
      if (pj?.id && !map.has(pj.id)) {
        const isStaleUnassigned =
          pj.status === 'open' &&
          !pj.selectedProfessionalId &&
          pj.createdAt &&
          Date.now() - new Date(pj.createdAt).getTime() >= 24 * 60 * 60 * 1000;
        if (!isStaleUnassigned) {
          map.set(pj.id, pj);
        }
      }
    });
    return Array.from(map.values()).sort(
      (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
    );
  }, []);

  const [quotes, setQuotes] = useState<Quote[]>(() =>
    safeGetStorage<Quote[]>('jobmatch_quotes', INITIAL_QUOTES)
  );

  const [messages, setMessages] = useState<Message[]>(() =>
    safeGetStorage<Message[]>('jobmatch_messages', INITIAL_MESSAGES)
  );

  const [notifications, setNotifications] = useState<Notification[]>(() =>
    safeGetStorage<Notification[]>('jobmatch_notifications', INITIAL_NOTIFICATIONS)
  );

  const [reviews] = useState<Review[]>(INITIAL_REVIEWS);

  const resolveInitialRoute = (): { view: AppView; params: ViewParams } => {
    if (typeof window === 'undefined') return { view: 'home', params: {} };
    const pathname = window.location.pathname.toLowerCase().replace(/\/$/, '');
    const hash = window.location.hash || '';
    const searchParams = new URLSearchParams(window.location.search);
    const viewParam = searchParams.get('view')?.toLowerCase().trim() || undefined;
    const urlCategory = searchParams.get('selectedCategory') || searchParams.get('category') || undefined;
    const urlJobId = searchParams.get('jobId') || searchParams.get('job_id') || searchParams.get('id') || undefined;
    const urlQuoteId = searchParams.get('quoteId') || searchParams.get('quote_id') || searchParams.get('highlightQuoteId') || undefined;
    const urlTab = searchParams.get('tab') || searchParams.get('activeTab') || undefined;
    const urlAdminTab = searchParams.get('adminTab') || searchParams.get('tab') || undefined;
    const urlEmail = searchParams.get('email') || undefined;
    const urlPhone = searchParams.get('phone') || undefined;
    const urlRef = searchParams.get('ref') || undefined;
    const urlAuthMode = (searchParams.get('authMode') === 'register' ? 'register' : 'login') as 'login' | 'register';
    const urlRole = (searchParams.get('role') as UserRole) || undefined;
    const urlProId =
      searchParams.get('proId') ||
      searchParams.get('pro_id') ||
      searchParams.get('professionalId') ||
      (pathname === '/professional-profile' ? searchParams.get('id') : undefined) ||
      undefined;
    const urlContactId =
      searchParams.get('contactId') ||
      searchParams.get('contact_id') ||
      searchParams.get('recipientId') ||
      undefined;

    const isRecoveryCallback =
      searchParams.get('type') === 'recovery' ||
      hash.includes('type=recovery') ||
      searchParams.get('mode') === 'reset';

    if (
      pathname === '/reset-password' ||
      pathname === '/password-reset' ||
      viewParam === 'password_reset' ||
      isRecoveryCallback
    ) {
      return { view: 'password_reset', params: { email: urlEmail, role: urlRole } };
    }

    if (pathname === '/auth/callback') {
      if (currentUser?.role === 'professional') {
        return { view: 'pro_dashboard', params: { jobId: urlJobId, tab: urlTab } };
      }
      if (currentUser?.role === 'customer') {
        return { view: 'customer_dashboard', params: { jobId: urlJobId, tab: urlTab } };
      }
      return {
        view: urlRole === 'professional' ? 'pro_auth' : 'customer_auth',
        params: { authMode: 'login', email: urlEmail },
      };
    }

    if (pathname === '/verify' || pathname === '/verify-email' || viewParam === 'verify') {
      if (currentUser?.role === 'professional') {
        return { view: 'pro_dashboard', params: { jobId: urlJobId, tab: urlTab } };
      }
      if (currentUser?.role === 'customer') {
        return { view: 'customer_dashboard', params: { jobId: urlJobId, tab: urlTab } };
      }
      return {
        view: urlRole === 'professional' ? 'pro_auth' : 'customer_auth',
        params: { authMode: 'login', email: urlEmail },
      };
    }

    if (pathname.startsWith('/services/')) {
      const slug = pathname.replace('/services/', '').trim();
      return { view: 'find_pro', params: slug ? { selectedCategory: slug } : {} };
    }
    if (pathname === '/find-a-professional' || viewParam === 'find_pro') {
      return { view: 'find_pro', params: urlCategory ? { selectedCategory: urlCategory } : {} };
    }
    if (pathname === '/how-it-works' || viewParam === 'how_it_works') return { view: 'how_it_works', params: {} };
    if (pathname === '/become-a-professional' || pathname === '/pricing' || viewParam === 'become_pro' || viewParam === 'pricing') {
      return { view: 'become_pro', params: {} };
    }
    if (pathname === '/for-customers' || pathname === '/customer-guide' || viewParam === 'customer_guide') {
      return { view: 'customer_guide', params: {} };
    }
    if (pathname === '/verification' || pathname === '/trust' || viewParam === 'verification') {
      return { view: 'verification', params: {} };
    }
    if (pathname === '/faq' || viewParam === 'faq') return { view: 'faq', params: {} };
    if (pathname === '/about' || viewParam === 'about') return { view: 'about', params: {} };
    if (pathname === '/contact' || viewParam === 'contact') return { view: 'contact', params: {} };
    if (pathname === '/privacy' || viewParam === 'privacy') return { view: 'privacy', params: {} };
    if (pathname === '/terms' || viewParam === 'terms') return { view: 'terms', params: {} };
    if (pathname === '/customer-login' || pathname === '/customer/login' || pathname === '/login') {
      return { view: 'customer_auth', params: { authMode: 'login', email: urlEmail, phone: urlPhone, ref: urlRef } };
    }
    if (pathname === '/customer-register' || pathname === '/register') {
      return { view: 'customer_auth', params: { authMode: 'register', email: urlEmail, phone: urlPhone, ref: urlRef } };
    }
    if (viewParam === 'customer_auth') {
      return { view: 'customer_auth', params: { authMode: urlAuthMode, email: urlEmail, phone: urlPhone, ref: urlRef } };
    }
    if (pathname === '/pro-login' || pathname === '/pro/login' || pathname === '/professional-login' || pathname === '/login/pro') {
      return { view: 'pro_auth', params: { authMode: 'login', email: urlEmail, phone: urlPhone, ref: urlRef } };
    }
    if (pathname === '/register-pro' || pathname === '/pro-register' || pathname === '/pro/register') {
      return { view: 'pro_auth', params: { authMode: 'register', email: urlEmail, phone: urlPhone, ref: urlRef } };
    }
    if (viewParam === 'pro_auth') {
      return { view: 'pro_auth', params: { authMode: urlAuthMode, email: urlEmail, phone: urlPhone, ref: urlRef } };
    }
    if (
      pathname === '/admin_auth' ||
      pathname === '/admin-auth' ||
      pathname === '/admin-login' ||
      pathname === '/admin/login' ||
      pathname === '/admin/auth' ||
      viewParam === 'admin_auth'
    ) {
      return { view: 'admin_auth', params: {} };
    }
    if (pathname === '/admin' || viewParam === 'admin' || viewParam === 'admin_dashboard') {
      return { view: 'admin_dashboard', params: { adminTab: urlAdminTab } };
    }
    if (pathname === '/professional-dashboard' || pathname === '/pro-dashboard' || viewParam === 'pro_dashboard') {
      return { view: 'pro_dashboard', params: { jobId: urlJobId, tab: urlTab, activeTab: urlTab, adminTab: urlAdminTab } };
    }
    // If customer was actively on PostJobPage (e.g. mobile camera reload in iframe/browser), preserve post_job view
    try {
      if (
        typeof window !== 'undefined' &&
        window.sessionStorage?.getItem('jobmatch_post_job_active') === 'true' &&
        (pathname === '/customer-dashboard' || pathname === '/dashboard' || pathname === '' || pathname === '/') &&
        !viewParam &&
        !searchParams.get('paystack_ref') &&
        !searchParams.get('reference') &&
        !searchParams.get('trxref')
      ) {
        return { view: 'post_job', params: { selectedCategory: urlCategory } };
      }
    } catch (_e) {}

    if (pathname === '/customer-dashboard' || viewParam === 'customer_dashboard') {
      return { view: 'customer_dashboard', params: { jobId: urlJobId, tab: urlTab, activeTab: urlTab, highlightQuoteId: urlQuoteId } };
    }
    if (pathname === '/dashboard' || viewParam === 'dashboard') {
      const targetDashboard: AppView = currentUser?.role === 'professional' ? 'pro_dashboard' : 'customer_dashboard';
      return { view: targetDashboard, params: { jobId: urlJobId, tab: urlTab, activeTab: urlTab, highlightQuoteId: urlQuoteId } };
    }
    if (pathname === '/available-jobs' || viewParam === 'available_jobs') {
      return { view: 'available_jobs', params: { jobId: urlJobId, selectedCategory: urlCategory } };
    }
    if (pathname === '/post-job' || viewParam === 'post_job') {
      return { view: 'post_job', params: { selectedCategory: urlCategory } };
    }
    if (pathname === '/quote-comparison' || viewParam === 'quote_comparison') {
      return { view: 'quote_comparison', params: { jobId: urlJobId, highlightQuoteId: urlQuoteId } };
    }
    if (pathname === '/pro-subscription' || viewParam === 'pro_subscription') return { view: 'pro_subscription', params: {} };
    if (pathname === '/professional-profile' || viewParam === 'pro_profile') return { view: 'pro_profile', params: { proId: urlProId } };
    if (pathname === '/messages' || viewParam === 'messages') {
      return { view: 'messages', params: { jobId: urlJobId, proId: urlProId, contactId: urlContactId } };
    }
    if (pathname === '/notifications' || viewParam === 'notifications') return { view: 'notifications', params: {} };
    if (
      pathname === '/job' ||
      pathname === '/jobs' ||
      pathname === '/job-details' ||
      pathname.startsWith('/job/') ||
      viewParam === 'job_details'
    ) {
      const pathJobId = pathname.startsWith('/job/') ? pathname.replace('/job/', '').split('/')[0].trim() : undefined;
      const effectiveJobId = urlJobId || pathJobId;
      return { view: 'job_details', params: { jobId: effectiveJobId } };
    }
    if (pathname === '' || pathname === '/' || viewParam === 'home') return { view: 'home', params: {} };
    return { view: 'not_found', params: {} };
  };

  const initialRoute = resolveInitialRoute();
  const [currentView, setCurrentView] = useState<AppView>(initialRoute.view);
  const [viewParams, setViewParams] = useState<ViewParams>(initialRoute.params);
  const currentViewRef = React.useRef<AppView>(initialRoute.view);
  const currentUserRef = React.useRef<Profile | null>(currentUser);

  useEffect(() => {
    currentViewRef.current = currentView;
  }, [currentView]);

  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);
  const [authModalState, setAuthModalState] = useState<{
    isOpen: boolean;
    mode: 'login' | 'register';
  }>({
    isOpen: false,
    mode: 'login',
  });

  const openAuthModal = useCallback((mode: 'login' | 'register' = 'login') => {
    setAuthModalState({
      isOpen: true,
      mode,
    });
  }, []);

  const closeAuthModal = useCallback(() => {
    setAuthModalState((prev) => ({
      ...prev,
      isOpen: false,
    }));
  }, []);

  // 1. Check Supabase Health & Fetch Live Data if migrated
  const checkHealth = useCallback(async () => {
    const status = await checkSupabaseHealth();
    setSupabaseHealth(status);

    if (status.tablesReady) {
      const [liveJobs, liveQuotes, liveMessages, liveProfiles] = await Promise.all([
        getSupabaseJobs(),
        getSupabaseQuotes(),
        getSupabaseMessages(),
        getSupabaseProfiles(),
      ]);
      if (Array.isArray(liveJobs) && liveJobs.length > 0) {
        setJobs((prev) => mergeLiveJobs(liveJobs, prev));
      }
      if (Array.isArray(liveQuotes)) {
        setQuotes(liveQuotes);
      }
      if (Array.isArray(liveMessages) && liveMessages.length > 0) {
        setMessages(liveMessages);
      }
      if (Array.isArray(liveProfiles) && liveProfiles.length > 0) {
        const cleanLive = liveProfiles.filter((lp) => !isFakeAccountProfile(lp));
        setProfiles(cleanLive);
        safeSetStorage('jobmatch_profiles', cleanLive);
      }
    }
  }, []);

  useEffect(() => {
    checkHealth();
  }, [checkHealth]);

  // Load live messages when currentUser changes
  useEffect(() => {
    if (currentUser?.id) {
      getSupabaseMessages(undefined, currentUser.id).then((liveMsgs) => {
        if (liveMsgs && liveMsgs.length > 0) {
          setMessages((prev) => {
            const map = new Map<string, Message>();
            prev.forEach((m) => map.set(m.id, m));
            liveMsgs.forEach((m) => map.set(m.id, m));
            return Array.from(map.values()).sort(
              (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
            );
          });
        }
      });
    }
  }, [currentUser?.id]);

  // Helper: Verify a Supabase user session (especially Google OAuth) against JobMatch NG security gates
  const verifyGoogleOrOAuthSession = useCallback(
    async (sessionUser: any, accessToken?: string, options?: { isOAuthRedirectCallback?: boolean }) => {
      if (!sessionUser?.id || !sessionUser?.email) return;

      const storedIntent = getStoredGoogleOAuthIntent();
      const meta = sessionUser.user_metadata || {};
      const appMeta = sessionUser.app_metadata || {};
      const isGoogleProvider =
        appMeta.provider === 'google' ||
        (Array.isArray(appMeta.providers) && appMeta.providers.includes('google')) ||
        Boolean(storedIntent);

      const activeView = currentViewRef.current;
      const isOnAuthView =
        activeView === 'customer_auth' ||
        activeView === 'pro_auth' ||
        activeView === 'admin_auth';
      let isPostJobSessionActive = activeView === 'post_job';
      try {
        if (typeof window !== 'undefined' && window.sessionStorage?.getItem('jobmatch_post_job_active') === 'true') {
          isPostJobSessionActive = true;
        }
      } catch (_e) {}

      // Only redirect to dashboard when completing an actual OAuth sign-in flow or sitting on an auth screen
      const shouldRedirectToDashboard =
        !isPostJobSessionActive &&
        (Boolean(storedIntent) || Boolean(options?.isOAuthRedirectCallback) || isOnAuthView);

      try {
        const res = await fetch('/api/auth/google/verify-status', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: sessionUser.id,
            email: sessionUser.email,
            fullName: meta.full_name || meta.name || '',
            avatarUrl: meta.avatar_url || meta.picture || '',
            requestedRole: storedIntent?.role || undefined,
            authMode: storedIntent?.authMode || 'login',
            accessToken,
          }),
        });
        const data = await res.json().catch(() => ({}));

        if (data.status === 'suspended') {
          await supabase.auth.signOut().catch(() => {});
          setCurrentUser(null);
          safeSetStorage('jobmatch_current_user', null);
          clearStoredGoogleOAuthIntent();
          setGoogleAuthNotice({
            type: 'error',
            title: 'Authentication failed',
            message: data.error || 'This account is currently suspended. Please contact JobMatch NG Support.',
          });
          return;
        }

        if (data.status === 'role_mismatch') {
          clearStoredGoogleOAuthIntent();
          // If a profile was returned, seamlessly authenticate into their valid registered role without error
          if (data.profile) {
            const resolvedProfile: Profile = {
              ...data.profile,
              emailVerified: true,
              phoneVerified: Boolean(data.profile.phoneVerified),
            };
            setPendingGoogleAuth(null);
            setCurrentUser(resolvedProfile);
            safeSetStorage('jobmatch_current_user', resolvedProfile);
            if (shouldRedirectToDashboard) {
              const target: AppView =
                resolvedProfile.role === 'professional' ? 'pro_dashboard' : 'customer_dashboard';
              setCurrentView(target);
              setGoogleAuthNotice({
                type: 'success',
                title: 'Signed In',
                message: `Welcome back, ${resolvedProfile.fullName}! You are signed in to your registered ${resolvedProfile.role} account.`,
              });
            }
            return;
          }
          // If no profile, navigate to the portal without killing session
          const targetView: AppView = storedIntent?.role === 'professional' ? 'pro_auth' : 'customer_auth';
          setGoogleAuthNotice({
            type: 'profile_required',
            title: 'Portal Selection',
            message: data.message || data.error || 'Please sign in on the correct portal for your account type.',
          });
          setCurrentView(targetView);
          return;
        }

        if (data.status === 'requires_device_verification' && data.challengeToken) {
          const prof = data.profile as Profile | undefined;
          setCurrentUser(null);
          safeSetStorage('jobmatch_current_user', null);
          setPendingGoogleAuth({
            status: 'requires_device_verification',
            requestedRole: 'customer',
            authMode: 'login',
            challengeToken: data.challengeToken,
            googleUser: {
              id: sessionUser.id,
              email: sessionUser.email,
              fullName: prof?.fullName || meta.full_name || meta.name || sessionUser.email.split('@')[0],
              avatarUrl: prof?.avatarUrl || meta.avatar_url || meta.picture || '',
              emailVerified: true,
              phoneVerified: true,
            },
            existingProfile: prof,
            message:
              data.message ||
              'New device detected. Please enter the 6-digit verification code sent to your email address.',
          });
          setGoogleAuthNotice({
            type: 'device_verification_required',
            title: 'New device verification required',
            message:
              data.message ||
              'We detected a sign-in from a new or unrecognized device. Please enter the 6-digit code sent to your email.',
          });
          setCurrentView('customer_auth');
          setViewParams((prev) => ({ ...prev, authMode: 'login', email: sessionUser.email }));
          return;
        }

        if (data.status === 'authenticated' && data.profile) {
          const verifiedProfile: Profile = {
            ...data.profile,
            emailVerified: true,
            phoneVerified: true,
          };
          setPendingGoogleAuth(null);
          clearStoredGoogleOAuthIntent();
          setCurrentUser(verifiedProfile);
          safeSetStorage('jobmatch_current_user', verifiedProfile);
          setProfiles((prev) => {
            const exists = prev.some((p) => p.id === verifiedProfile.id);
            return exists ? prev.map((p) => (p.id === verifiedProfile.id ? verifiedProfile : p)) : [verifiedProfile, ...prev];
          });

          if (isGoogleProvider && shouldRedirectToDashboard) {
            setGoogleAuthNotice({
              type: 'success',
              title: 'Google login successful',
              message: `Welcome back, ${verifiedProfile.fullName}! You are signed in with Google.`,
            });
            const target: AppView =
              (storedIntent?.returnView as AppView) ||
              (verifiedProfile.role === 'professional' ? 'pro_dashboard' : 'customer_dashboard');
            setCurrentView(target);
          }
          return;
        }

        if (data.status === 'professional_approval_pending' && data.profile) {
          const pendingProProfile: Profile = {
            ...data.profile,
            emailVerified: true,
            phoneVerified: true,
            verificationStatus: data.profile.verificationStatus || 'pending',
            professionalApprovalStatus: data.profile.professionalApprovalStatus || 'pending',
          };
          setPendingGoogleAuth({
            status: 'professional_approval_pending',
            requestedRole: 'professional',
            authMode: storedIntent?.authMode || 'login',
            googleUser: {
              id: sessionUser.id,
              email: sessionUser.email,
              fullName: pendingProProfile.fullName,
              avatarUrl: pendingProProfile.avatarUrl,
              emailVerified: true,
              phoneVerified: true,
            },
            existingProfile: pendingProProfile,
            message:
              data.message ||
              'Professional approval pending. Your professional application and verification documents are currently under administrator review.',
          });
          clearStoredGoogleOAuthIntent();
          setCurrentUser(pendingProProfile);
          safeSetStorage('jobmatch_current_user', pendingProProfile);
          setGoogleAuthNotice({
            type: 'approval_pending',
            title: 'Professional approval pending',
            message:
              data.message ||
              'Your professional application and verification documents are currently under administrator review.',
          });
          if (storedIntent) {
            setCurrentView('pro_dashboard');
          }
          return;
        }

        if (data.status === 'requires_phone_verification' && data.profile) {
          const prof = {
            ...(data.profile as Profile),
            emailVerified: true,
            phoneVerified: true,
            onboardingStatus: 'completed' as const,
          };
          setCurrentUser(prof);
          safeSetStorage('jobmatch_current_user', prof);
          setPendingGoogleAuth(null);
          setGoogleAuthNotice(null);
          if (storedIntent) {
            setCurrentView(prof.role === 'professional' ? 'pro_dashboard' : 'customer_dashboard');
          }
          return;
        }

        if (data.status === 'requires_profile_completion') {
          const prof = data.profile as Profile | undefined;
          const roleTarget: 'customer' | 'professional' =
            prof?.role === 'professional' || storedIntent?.role === 'professional' ? 'professional' : 'customer';
          setCurrentUser(null);
          safeSetStorage('jobmatch_current_user', null);
          setPendingGoogleAuth({
            status: 'requires_onboarding',
            requestedRole: roleTarget,
            authMode: 'register',
            googleUser: {
              id: sessionUser.id,
              email: sessionUser.email,
              fullName: prof?.fullName || meta.full_name || meta.name || '',
              avatarUrl: prof?.avatarUrl || meta.avatar_url || meta.picture || '',
              emailVerified: true,
              phoneVerified: Boolean(prof?.phoneVerified),
            },
            existingProfile: prof,
            message:
              data.message ||
              'Profile completion required. Please complete your registration details to activate your account.',
          });
          setGoogleAuthNotice({
            type: 'profile_required',
            title: 'Profile completion required',
            message:
              data.message ||
              'Please complete your profile and verification requirements to finish setting up your JobMatch NG account.',
          });
          setCurrentView(roleTarget === 'professional' ? 'pro_auth' : 'customer_auth');
          setViewParams((prev) => ({ ...prev, authMode: 'register' }));
          return;
        }

        if (data.status === 'requires_account_linking') {
          const existingRole: 'customer' | 'professional' =
            data.existingRole === 'professional' ? 'professional' : 'customer';
          setCurrentUser(null);
          safeSetStorage('jobmatch_current_user', null);
          setPendingGoogleAuth({
            status: 'requires_account_linking',
            requestedRole: existingRole,
            authMode: 'login',
            googleUser: {
              id: sessionUser.id,
              email: sessionUser.email,
              fullName: meta.full_name || meta.name || '',
              avatarUrl: meta.avatar_url || meta.picture || '',
              emailVerified: true,
              phoneVerified: false,
            },
            existingAccount: {
              existingRole,
              maskedEmail: data.maskedEmail || sessionUser.email,
              maskedPhone: data.maskedPhone || null,
            },
            message:
              data.message ||
              'An account with this email address already exists. Please verify ownership of your existing account to securely link Google Sign-In.',
          });
          setGoogleAuthNotice({
            type: 'linking_required',
            title: 'Existing Account Verification Required',
            message:
              data.message ||
              'An account with this email address already exists on JobMatch NG. To protect your account, please verify ownership before linking Google Sign-In.',
          });
          setCurrentView(existingRole === 'professional' ? 'pro_auth' : 'customer_auth');
          setViewParams((prev) => ({ ...prev, authMode: 'login' }));
          return;
        }

        if (data.status === 'requires_role_selection') {
          setCurrentUser(null);
          safeSetStorage('jobmatch_current_user', null);
          setPendingGoogleAuth({
            status: 'requires_role_selection',
            requestedRole: null,
            authMode: 'register',
            googleUser: data.googleUser || {
              id: sessionUser.id,
              email: sessionUser.email,
              fullName: meta.full_name || meta.name || '',
              avatarUrl: meta.avatar_url || meta.picture || '',
              emailVerified: true,
              phoneVerified: false,
            },
            message:
              data.message ||
              'Profile completion required. Please select your account type (Customer or Professional) to continue registration.',
          });
          setGoogleAuthNotice({
            type: 'profile_required',
            title: 'Profile completion required',
            message: 'Google email verified. Please select whether you are joining as a Customer or a Professional.',
          });
          setAuthModalState({ isOpen: true, mode: 'register' });
          return;
        }

        if (data.status === 'requires_onboarding') {
          const requestedRole: 'customer' | 'professional' =
            data.requestedRole === 'professional' || storedIntent?.role === 'professional'
              ? 'professional'
              : 'customer';
          setCurrentUser(null);
          safeSetStorage('jobmatch_current_user', null);
          setPendingGoogleAuth({
            status: 'requires_onboarding',
            requestedRole,
            authMode: 'register',
            googleUser: data.googleUser || {
              id: sessionUser.id,
              email: sessionUser.email,
              fullName: meta.full_name || meta.name || '',
              avatarUrl: meta.avatar_url || meta.picture || '',
              emailVerified: true,
              phoneVerified: false,
            },
            message:
              data.message ||
              'Phone verification required & Profile completion required. Please complete your registration details and verify your Nigerian phone number via SMS OTP.',
          });
          setGoogleAuthNotice({
            type: 'phone_required',
            title: 'Phone verification & profile completion required',
            message:
              'Google login successful! Complete your profile details and verify your Nigerian mobile number via SMS OTP to activate your account.',
          });
          setCurrentView(requestedRole === 'professional' ? 'pro_auth' : 'customer_auth');
          setViewParams((prev) => ({ ...prev, authMode: 'register' }));
          return;
        }
      } catch (err) {
        console.warn('Google OAuth verify-status check failed:', err);
      }
    },
    []
  );

  // 2. Initialize and Verify Supabase Session on Mount (including OAuth callback handling)
  useEffect(() => {
    let isMounted = true;

    const initAuthSession = async () => {
      try {
        setAuthLoading(true);

        // Check URL for OAuth callback errors or cancellation (e.g., ?error=access_denied)
        if (typeof window !== 'undefined') {
          const searchParams = new URLSearchParams(window.location.search);
          const hashParams = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
          const oauthError = searchParams.get('error') || hashParams.get('error');
          const oauthErrorDesc =
            searchParams.get('error_description') || hashParams.get('error_description') || '';

          if (oauthError) {
            const storedIntent = getStoredGoogleOAuthIntent();
            clearStoredGoogleOAuthIntent();
            const isCancelled =
              oauthError === 'access_denied' ||
              oauthErrorDesc.toLowerCase().includes('cancel') ||
              oauthErrorDesc.toLowerCase().includes('denied') ||
              oauthErrorDesc.toLowerCase().includes('closed');

            setGoogleAuthNotice({
              type: isCancelled ? 'cancelled' : 'error',
              title: isCancelled ? 'Google login cancelled' : 'Authentication failed',
              message: isCancelled
                ? 'Google login cancelled. You can try again anytime or sign in with your email and password.'
                : 'Authentication failed. We could not complete Google Sign-In. Please try again or use your email and password.',
            });

            // Clean error query/hash from browser URL
            try {
              const cleanUrl = new URL(window.location.href);
              cleanUrl.searchParams.delete('error');
              cleanUrl.searchParams.delete('error_code');
              cleanUrl.searchParams.delete('error_description');
              if (cleanUrl.hash.includes('error=')) {
                cleanUrl.hash = '';
              }
              window.history.replaceState({}, '', `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);
            } catch (_e) {}

            if (storedIntent?.role === 'professional') {
              setCurrentView('pro_auth');
              setViewParams((prev) => ({ ...prev, authMode: storedIntent.authMode || 'login' }));
            } else if (storedIntent?.role === 'customer') {
              setCurrentView('customer_auth');
              setViewParams((prev) => ({ ...prev, authMode: storedIntent.authMode || 'login' }));
            }
          }
        }

        const { data: { session }, error: sessionErr } = await supabase.auth.getSession();
        if (!isMounted) return;

        if (sessionErr) {
          console.warn('Session verification notice:', sessionErr.message);
        }

        if (session?.user) {
          const storedIntent = getStoredGoogleOAuthIntent();
          const appMeta = session.user.app_metadata || {};
          const isGoogleUser =
            appMeta.provider === 'google' ||
            (Array.isArray(appMeta.providers) && appMeta.providers.includes('google')) ||
            Boolean(storedIntent);

          // Clean OAuth code/hash tokens from URL if present (never during password recovery flows!)
          let hadOAuthCallbackTokens = false;
          if (typeof window !== 'undefined') {
            const isRecoveryFlow =
              window.location.pathname === '/reset-password' ||
              window.location.pathname === '/password-reset' ||
              window.location.hash.includes('type=recovery') ||
              window.location.search.includes('type=recovery') ||
              window.location.search.includes('token_hash');

            if (!isRecoveryFlow) {
              try {
                const cleanUrl = new URL(window.location.href);
                if (
                  cleanUrl.searchParams.has('code') ||
                  cleanUrl.hash.includes('access_token=') ||
                  cleanUrl.pathname === '/auth/callback'
                ) {
                  hadOAuthCallbackTokens = true;
                  cleanUrl.searchParams.delete('code');
                  if (cleanUrl.hash.includes('access_token=')) {
                    cleanUrl.hash = '';
                  }
                  window.history.replaceState({}, '', `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);
                }
              } catch (_e) {}
            }
          }

          if (isGoogleUser) {
            await verifyGoogleOrOAuthSession(session.user, session.access_token, {
              isOAuthRedirectCallback: hadOAuthCallbackTokens,
            });
          } else {
            const userProfile = await fetchProfileForAuthUser(
              session.user.id,
              session.user.email,
              profiles
            );

            if (isMounted) {
              if (userProfile) {
                if (userProfile.isSuspended) {
                  await supabase.auth.signOut().catch(() => {});
                  setCurrentUser(null);
                  safeSetStorage('jobmatch_current_user', null);
                } else {
                  setCurrentUser(userProfile);
                  safeSetStorage('jobmatch_current_user', userProfile);
                }
              } else {
                // Never auto-assign a role or mark phoneVerified: true for an unknown auth user
                await verifyGoogleOrOAuthSession(session.user, session.access_token, {
                  isOAuthRedirectCallback: hadOAuthCallbackTokens,
                });
              }
            }
          }
        } else {
          // No active session in Supabase Auth
          if (isMounted) {
            setCurrentUser(null);
            safeSetStorage('jobmatch_current_user', null);
            safeSetStorage('jobmatch_admin_session', null);
          }
        }
      } catch (err) {
        console.warn('Session verification notice:', err);
        if (isMounted) {
          setCurrentUser(null);
          safeSetStorage('jobmatch_current_user', null);
        }
      } finally {
        if (isMounted) {
          setAuthLoading(false);
        }
      }
    };

    initAuthSession();

    // 3. Listen to Supabase Auth State Changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setCurrentView('password_reset');
        return;
      } else if (event === 'SIGNED_OUT') {
        setCurrentUser(null);
        safeSetStorage('jobmatch_current_user', null);
        safeSetStorage('jobmatch_admin_session', null);
        safeSetStorage('jobmatch_2fa_verified_token', null);
      } else if (event === 'SIGNED_IN' && session?.user) {
        // If recovery in progress or currently on password reset page, stay on password_reset view
        if (
          typeof window !== 'undefined' &&
          (window.location.pathname === '/reset-password' ||
            window.location.pathname === '/password-reset' ||
            window.location.hash.includes('type=recovery') ||
            window.location.search.includes('type=recovery'))
        ) {
          setCurrentView('password_reset');
          return;
        }

        const storedIntent = getStoredGoogleOAuthIntent();
        const activeUser = currentUserRef.current;
        const activeView = currentViewRef.current;
        const isAlreadyVerifiedCurrentUser =
          Boolean(activeUser && activeUser.id === session.user.id);
        const isOnAuthScreen =
          activeView === 'customer_auth' || activeView === 'pro_auth' || activeView === 'admin_auth';

        // Ignore background visibility/camera focus SIGNED_IN refreshes when user is already authenticated on an app view
        if (isAlreadyVerifiedCurrentUser && !storedIntent && !isOnAuthScreen) {
          return;
        }

        const appMeta = session.user.app_metadata || {};
        const isGoogleUser =
          appMeta.provider === 'google' ||
          (Array.isArray(appMeta.providers) && appMeta.providers.includes('google')) ||
          Boolean(storedIntent);

        if (isGoogleUser) {
          await verifyGoogleOrOAuthSession(session.user, session.access_token, {
            isOAuthRedirectCallback: Boolean(storedIntent),
          });
          return;
        }

        const userProfile = await fetchProfileForAuthUser(
          session.user.id,
          session.user.email,
          profiles
        );
        if (userProfile && !userProfile.isSuspended) {
          // Guard against cross-role auto-login on dedicated auth pages
          if (
            (currentViewRef.current === 'customer_auth' && userProfile.role === 'professional') ||
            (currentViewRef.current === 'pro_auth' && userProfile.role === 'customer')
          ) {
            return;
          }
          setCurrentUser(userProfile);
          safeSetStorage('jobmatch_current_user', userProfile);
        }
      }
    });

    return () => {
      isMounted = false;
      subscription?.unsubscribe();
    };
  }, [verifyGoogleOrOAuthSession]);

  const [onlineUserIds, setOnlineUserIds] = useState<string[]>([]);

  // 4. Setup Realtime subscriptions if tables ready
  useEffect(() => {
    if (!supabaseHealth.tablesReady) return;

    const channel = supabase
      .channel('jobmatch-realtime', {
        config: {
          presence: {
            key: currentUser?.id || 'anon',
          },
        },
      })
      .on('presence', { event: 'sync' }, () => {
        try {
          const state = channel.presenceState();
          const ids = new Set<string>();
          Object.entries(state).forEach(([key, presences]) => {
            if (key && key !== 'anon') ids.add(key);
            if (Array.isArray(presences)) {
              presences.forEach((p: any) => {
                if (p?.user_id) ids.add(String(p.user_id));
              });
            }
          });
          setOnlineUserIds(Array.from(ids));
        } catch (_) {}
      })
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'jobs' },
        async () => {
          const [updatedJobs, updatedQuotes] = await Promise.all([
            getSupabaseJobs(),
            getSupabaseQuotes(),
          ]);
          if (Array.isArray(updatedJobs) && updatedJobs.length > 0) {
            setJobs((prev) => mergeLiveJobs(updatedJobs, prev));
          }
          if (Array.isArray(updatedQuotes)) setQuotes(updatedQuotes);
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'quotes' },
        async () => {
          const [updatedJobs, updatedQuotes] = await Promise.all([
            getSupabaseJobs(),
            getSupabaseQuotes(),
          ]);
          if (Array.isArray(updatedJobs)) setJobs(updatedJobs);
          if (Array.isArray(updatedQuotes)) setQuotes(updatedQuotes);
        }
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        (payload) => {
          const newMsg = payload.new as any;
          if (!newMsg || !newMsg.id) return;

          setMessages((prev) => {
            if (prev.some((m) => m.id === newMsg.id)) return prev;

            const senderProfile = profiles.find((p) => p.id === newMsg.sender_id);
            const formatted: Message = {
              id: newMsg.id,
              jobId: newMsg.job_id || null,
              conversationId: newMsg.conversation_id || undefined,
              senderId: newMsg.sender_id,
              senderName:
                senderProfile?.fullName ||
                senderProfile?.businessName ||
                (newMsg.sender_id === currentUser?.id ? (currentUser?.fullName || 'You') : 'Artisan / Customer'),
              senderRole: (senderProfile?.role ||
                (newMsg.sender_id === currentUser?.id ? currentUser?.role : undefined) ||
                'customer') as UserRole,
              senderAvatar: senderProfile?.avatarUrl || '',
              receiverId: newMsg.receiver_id,
              content: newMsg.content,
              attachments: Array.isArray(newMsg.attachments) ? newMsg.attachments : [],
              read: Boolean(newMsg.read ?? newMsg.is_read),
              delivered: true,
              readAt: newMsg.read_at || undefined,
              createdAt: newMsg.created_at || new Date().toISOString(),
            };

            return [...prev, formatted];
          });

          // In-app notification for recipient
          if (currentUser?.id && newMsg.receiver_id === currentUser.id && newMsg.sender_id !== currentUser.id) {
            const senderProfile = profiles.find((p) => p.id === newMsg.sender_id);
            const senderName = senderProfile?.fullName || senderProfile?.businessName || 'Artisan / Customer';
            const notif: Notification = {
              id: `notif-${Date.now()}`,
              userId: currentUser.id,
              title: `New Message from ${senderName}`,
              message: newMsg.content.length > 50 ? `${newMsg.content.substring(0, 50)}...` : newMsg.content,
              type: 'new_message',
              targetView: 'messages',
              targetJobId: newMsg.job_id || undefined,
              linkUrl: newMsg.job_id
                ? `/messages?jobId=${encodeURIComponent(newMsg.job_id)}`
                : `/messages?contactId=${encodeURIComponent(newMsg.sender_id)}`,
              read: false,
              createdAt: new Date().toISOString(),
            };
            setNotifications((prev) => [notif, ...prev]);
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'messages' },
        (payload) => {
          const updatedMsg = payload.new as any;
          if (!updatedMsg || !updatedMsg.id) return;
          setMessages((prev) =>
            prev.map((m) =>
              m.id === updatedMsg.id
                ? {
                    ...m,
                    read: Boolean(updatedMsg.read ?? updatedMsg.is_read ?? m.read),
                    readAt: updatedMsg.read_at || m.readAt,
                  }
                : m
            )
          );
        }
      )
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED' && currentUser?.id) {
          try {
            await channel.track({
              user_id: currentUser.id,
              online_at: new Date().toISOString(),
            });
          } catch (_) {}
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [supabaseHealth.tablesReady, profiles, currentUser?.id, currentUser?.fullName, currentUser?.role]);

  // Sync datasets to localStorage
  useEffect(() => {
    safeSetStorage('jobmatch_profiles', profiles);
  }, [profiles]);

  useEffect(() => {
    safeSetStorage('jobmatch_jobs', jobs);
  }, [jobs]);

  useEffect(() => {
    safeSetStorage('jobmatch_quotes', quotes);
  }, [quotes]);

  useEffect(() => {
    safeSetStorage('jobmatch_messages', messages);
  }, [messages]);

  useEffect(() => {
    safeSetStorage('jobmatch_notifications', notifications);
  }, [notifications]);

  const navigateTo = (view: AppView, params: ViewParams = {}) => {
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        if (view === 'post_job') {
          window.sessionStorage.setItem('jobmatch_post_job_active', 'true');
        } else {
          window.sessionStorage.removeItem('jobmatch_post_job_active');
        }
      }
    } catch (_e) {}

    setCurrentView(view);
    setViewParams(params);
    setAuthModalState((prev) => ({ ...prev, isOpen: false }));
    window.scrollTo({ top: 0, behavior: 'smooth' });

    try {
      const routeMap: Partial<Record<AppView, string>> = {
        home: '/',
        pro_dashboard: '/professional-dashboard',
        customer_dashboard: '/customer-dashboard',
        admin_dashboard: '/admin',
        pro_auth: params.authMode === 'register' ? '/register-pro' : '/pro-login',
        customer_auth: params.authMode === 'register' ? '/customer-register' : '/customer-login',
        admin_auth:
          typeof window !== 'undefined' &&
          (window.location.pathname === '/admin-login' || window.location.pathname === '/admin/login')
            ? window.location.pathname
            : '/admin_auth',
        become_pro: '/become-a-professional',
        find_pro:
          params.selectedCategory && params.selectedCategory !== 'other'
            ? `/services/${encodeURIComponent(params.selectedCategory)}`
            : '/find-a-professional',
        how_it_works: '/how-it-works',
        customer_guide: '/for-customers',
        verification: '/verification',
        available_jobs: '/available-jobs',
        post_job: '/post-job',
        quote_comparison: '/quote-comparison',
        job_details: params.jobId ? `/job?id=${encodeURIComponent(params.jobId)}` : '/jobs',
        pro_subscription: '/pro-subscription',
        pro_profile: '/professional-profile',
        messages: '/messages',
        notifications: '/notifications',
        password_reset: '/reset-password',
        faq: '/faq',
        about: '/about',
        contact: '/contact',
        privacy: '/privacy',
        terms: '/terms',
      };
      const path = routeMap[view];
      if (path) {
        // Never wipe recovery query params or hash fragments when routing to password_reset
        if (view === 'password_reset' && typeof window !== 'undefined' && window.location.pathname === '/reset-password') {
          return;
        }

        let search = '';
        if (params.jobId) {
          search += `?jobId=${encodeURIComponent(params.jobId)}`;
        }
        if (params.adminTab) {
          search += search ? `&adminTab=${encodeURIComponent(params.adminTab)}` : `?adminTab=${encodeURIComponent(params.adminTab)}`;
        }
        if (params.selectedCategory && view !== 'find_pro') {
          search += search ? `&selectedCategory=${encodeURIComponent(params.selectedCategory)}` : `?selectedCategory=${encodeURIComponent(params.selectedCategory)}`;
        }
        if (params.proId) {
          search += search ? `&proId=${encodeURIComponent(params.proId)}` : `?proId=${encodeURIComponent(params.proId)}`;
        }
        if (params.contactId) {
          search += search ? `&contactId=${encodeURIComponent(params.contactId)}` : `?contactId=${encodeURIComponent(params.contactId)}`;
        }
        const targetUrl = path + search;
        if (window.location.pathname + window.location.search !== targetUrl) {
          window.history.pushState({ view, params }, '', targetUrl);
        }
      }
    } catch (_e) {
      // Ignore history API errors in sandboxed environments
    }
  };

  // Synchronize professional subscription from server & database
  const refreshSubscriptionStatus = useCallback(
    async (proId?: string) => {
      const targetId = proId || currentUser?.id;
      if (!targetId) return;

      try {
        const subData = await fetchProfessionalSubscription(targetId);
        if (subData && subData.status) {
          setCurrentUser((prev) => {
            if (prev && prev.id === targetId) {
              return {
                ...prev,
                subscriptionStatus: subData.status,
                subscription: subData.subscription || undefined,
              };
            }
            return prev;
          });

          setProfiles((prev) =>
            prev.map((p) =>
              p.id === targetId
                ? {
                    ...p,
                    subscriptionStatus: subData.status,
                    subscription: subData.subscription || undefined,
                  }
                : p
            )
          );
        }
      } catch (err) {
        console.info('Subscription status refresh notice:', err);
      }
    },
    [currentUser?.id]
  );

  // Refresh authoritative jobs, quotes, and profiles from Supabase
  const refreshJobsAndQuotes = useCallback(async () => {
    try {
      const [liveJobs, liveQuotes, liveProfiles] = await Promise.all([
        getSupabaseJobs(),
        getSupabaseQuotes(),
        getSupabaseProfiles(),
      ]);
      if (Array.isArray(liveJobs) && liveJobs.length > 0) {
        setJobs((prev) => mergeLiveJobs(liveJobs, prev));
      }
      if (Array.isArray(liveQuotes)) {
        setQuotes(liveQuotes);
      }
      if (Array.isArray(liveProfiles) && liveProfiles.length > 0) {
        const cleanLive = liveProfiles.filter((lp) => !isFakeAccountProfile(lp));
        setProfiles(cleanLive);
        safeSetStorage('jobmatch_profiles', cleanLive);
      }
    } catch (e) {
      console.warn('refreshJobsAndQuotes notice:', e);
    }
  }, []);

  // Check Paystack transaction callback, URL pathname, & admin routes
  useEffect(() => {
    const handleUrlRouting = () => {
      const pathname = window.location.pathname.toLowerCase().replace(/\/$/, '');
      const hash = window.location.hash || '';
      const params = new URLSearchParams(window.location.search);
      const ref = params.get('paystack_ref') || params.get('reference') || params.get('trxref');
      const proView = params.get('pro_view');
      const viewParam = params.get('view');
      const adminTab = params.get('tab') || params.get('adminTab');

      const isRecoveryHash =
        hash.includes('type=recovery') ||
        ((pathname === '/reset-password' || pathname === '/password-reset') &&
          (hash.includes('access_token') || hash.includes('error_code=') || hash.includes('error=')));
      const isRecoveryQuery =
        params.get('type') === 'recovery' ||
        params.get('mode') === 'reset' ||
        ((pathname === '/reset-password' || pathname === '/password-reset') &&
          (params.has('code') || params.has('error') || params.has('error_code')));

      const urlJobId = params.get('jobId') || params.get('job_id') || params.get('id');
      const urlQuoteId = params.get('quoteId') || params.get('quote_id') || params.get('highlightQuoteId');
      const urlProId = params.get('proId') || params.get('pro_id') || params.get('professionalId');
      const urlContactId = params.get('contactId') || params.get('contact_id') || params.get('recipientId');
      const urlCategory = params.get('selectedCategory') || params.get('category');
      const urlTab = params.get('tab') || params.get('activeTab');
      const urlEmail = params.get('email') || undefined;
      const urlPhone = params.get('phone') || undefined;
      const urlRef = params.get('ref') || undefined;
      const urlAuthMode = (params.get('authMode') === 'register' ? 'register' : 'login') as 'login' | 'register';
      const urlRole = (params.get('role') as UserRole) || undefined;

      if (
        pathname === '/reset-password' ||
        pathname === '/password-reset' ||
        viewParam === 'password_reset' ||
        isRecoveryHash ||
        isRecoveryQuery
      ) {
        setCurrentView('password_reset');
        setViewParams({
          email: urlEmail,
          role: urlRole,
        });
        setAuthModalState((prev) => ({ ...prev, isOpen: false }));
      } else if (ref) {
        const paymentStatus = params.get('payment');
        const jobId = urlJobId;
        const proId = urlProId;
        const isHired = params.get('hired') === 'true';

        // Refresh database state so job status and quote updates are immediately live
        checkHealth();

        if (ref.startsWith('mkt_job_') || isHired || viewParam === 'customer_dashboard' || viewParam === 'quote_comparison') {
          if (jobId) {
            setJobs((prev) =>
              prev.map((j) =>
                j.id === jobId
                  ? {
                      ...j,
                      status: j.status === 'completed' ? 'completed' : 'in_progress',
                      paymentStatus: 'held_in_escrow' as any,
                      payment_status: 'held_in_escrow' as any,
                      escrow_held: true,
                      payment_reference: ref,
                      selectedProfessionalId: proId || j.selectedProfessionalId,
                    }
                  : j
              )
            );
          }
          verifyMarketplacePayment(ref)
            .catch(() => {})
            .finally(() => {
              refreshJobsAndQuotes();
            });
          navigateTo('customer_dashboard', {
            tab: 'active',
            activeTab: 'active',
            jobId: jobId || undefined,
            proId: proId || undefined,
            paystackRef: ref,
            paymentStatus: paymentStatus || 'success',
          });
        } else if (viewParam === 'messages') {
          refreshJobsAndQuotes();
          navigateTo('messages', {
            jobId: jobId || undefined,
            proId: proId || undefined,
            paystackRef: ref,
            paymentStatus: paymentStatus || undefined,
          });
        } else {
          refreshJobsAndQuotes();
          navigateTo('pro_subscription', { paystackRef: ref, subscriptionVerified: paymentStatus === 'success' });
        }
      } else if (pathname === '/auth/callback' || pathname === '/verify' || pathname === '/verify-email' || viewParam === 'verify') {
        if (isRecoveryHash || isRecoveryQuery) {
          setCurrentView('password_reset');
          setViewParams({ email: urlEmail, role: urlRole });
          return;
        }
        if (currentUser && currentUser.role === 'professional') {
          navigateTo('pro_dashboard', { jobId: urlJobId || undefined, tab: urlTab || undefined });
        } else if (currentUser && currentUser.role === 'customer') {
          navigateTo('customer_dashboard', { jobId: urlJobId || undefined, tab: urlTab || undefined });
        } else if (urlRole === 'professional') {
          navigateTo('pro_auth', { authMode: 'login', email: urlEmail });
        } else {
          navigateTo('customer_auth', { authMode: 'login', email: urlEmail });
        }
      } else if (proView === 'pro_subscription' || pathname === '/pro-subscription' || viewParam === 'pro_subscription') {
        navigateTo('pro_subscription');
      } else if (
        pathname === '/admin_auth' ||
        pathname === '/admin_auth/' ||
        pathname === '/admin-auth' ||
        pathname === '/admin/auth' ||
        pathname === '/admin/login' ||
        pathname === '/admin-login' ||
        viewParam === 'admin_auth'
      ) {
        navigateTo('admin_auth');
      } else if (
        viewParam === 'admin' ||
        viewParam === 'admin_dashboard' ||
        pathname === '/admin' ||
        pathname === '/admin/' ||
        adminTab === 'telemetry' ||
        adminTab === 'ai_monitoring' ||
        window.location.hash.includes('telemetry')
      ) {
        if (currentUser && currentUser.role === 'admin') {
          navigateTo('admin_dashboard', {
            adminTab: adminTab === 'telemetry' ? 'ai_monitoring' : (adminTab || undefined),
          });
        } else {
          navigateTo('admin_auth');
        }
      } else if (pathname === '/professional-dashboard' || pathname === '/pro-dashboard' || viewParam === 'pro_dashboard') {
        navigateTo('pro_dashboard', { jobId: urlJobId || undefined, tab: urlTab || undefined, activeTab: urlTab || undefined, adminTab: adminTab || undefined });
      } else if (
        typeof window !== 'undefined' &&
        window.sessionStorage?.getItem('jobmatch_post_job_active') === 'true' &&
        (pathname === '/customer-dashboard' || pathname === '/dashboard') &&
        !viewParam
      ) {
        setCurrentView('post_job');
      } else if (pathname === '/customer-dashboard' || viewParam === 'customer_dashboard') {
        navigateTo('customer_dashboard', { jobId: urlJobId || undefined, tab: urlTab || undefined, activeTab: urlTab || undefined, highlightQuoteId: urlQuoteId || undefined });
      } else if (pathname === '/dashboard' || viewParam === 'dashboard') {
        if (currentUser && currentUser.role === 'professional') {
          navigateTo('pro_dashboard', { jobId: urlJobId || undefined, tab: urlTab || undefined, activeTab: urlTab || undefined });
        } else {
          navigateTo('customer_dashboard', { jobId: urlJobId || undefined, tab: urlTab || undefined, activeTab: urlTab || undefined, highlightQuoteId: urlQuoteId || undefined });
        }
      } else if (
        pathname === '/pro-login' ||
        pathname === '/pro/login' ||
        pathname === '/professional-login' ||
        pathname === '/login/pro'
      ) {
        navigateTo('pro_auth', { authMode: 'login', email: urlEmail, phone: urlPhone, ref: urlRef });
      } else if (
        pathname === '/register-pro' ||
        pathname === '/pro-register' ||
        pathname === '/pro/register'
      ) {
        navigateTo('pro_auth', { authMode: 'register', email: urlEmail, phone: urlPhone, ref: urlRef });
      } else if (viewParam === 'pro_auth') {
        navigateTo('pro_auth', { authMode: urlAuthMode, email: urlEmail, phone: urlPhone, ref: urlRef });
      } else if (
        pathname === '/customer-login' ||
        pathname === '/customer/login' ||
        pathname === '/login'
      ) {
        navigateTo('customer_auth', { authMode: 'login', email: urlEmail, phone: urlPhone, ref: urlRef });
      } else if (pathname === '/customer-register' || pathname === '/register') {
        navigateTo('customer_auth', { authMode: 'register', email: urlEmail, phone: urlPhone, ref: urlRef });
      } else if (viewParam === 'customer_auth') {
        navigateTo('customer_auth', { authMode: urlAuthMode, email: urlEmail, phone: urlPhone, ref: urlRef });
      } else if (pathname === '/available-jobs' || viewParam === 'available_jobs') {
        navigateTo('available_jobs', { jobId: urlJobId || undefined, selectedCategory: urlCategory || undefined });
      } else if (pathname === '/post-job' || viewParam === 'post_job') {
        navigateTo('post_job', { selectedCategory: urlCategory || undefined });
      } else if (pathname === '/quote-comparison' || viewParam === 'quote_comparison') {
        navigateTo('quote_comparison', { jobId: urlJobId || undefined, highlightQuoteId: urlQuoteId || undefined });
      } else if (pathname === '/messages' || viewParam === 'messages') {
        navigateTo('messages', {
          jobId: urlJobId || undefined,
          proId: urlProId || undefined,
          contactId: urlContactId || undefined,
        });
      } else if (pathname === '/notifications' || viewParam === 'notifications') {
        navigateTo('notifications');
      } else if (pathname === '/professional-profile' || viewParam === 'pro_profile') {
        navigateTo('pro_profile', { proId: urlProId || undefined });
      } else if (
        pathname === '/job' ||
        pathname === '/jobs' ||
        pathname === '/job-details' ||
        pathname.startsWith('/job/') ||
        viewParam === 'job_details'
      ) {
        const pathJobId = pathname.startsWith('/job/') ? pathname.replace('/job/', '').split('/')[0].trim() : undefined;
        const effectiveJobId = urlJobId || pathJobId;
        navigateTo('job_details', { jobId: effectiveJobId || undefined });
      } else if (pathname.startsWith('/services/')) {
        const serviceSlug = pathname.replace('/services/', '').trim();
        if (serviceSlug) {
          setCurrentView('find_pro');
          setViewParams({ selectedCategory: serviceSlug });
        } else {
          navigateTo('find_pro');
        }
      } else if (pathname === '/find-a-professional' || viewParam === 'find_pro') {
        navigateTo('find_pro', { selectedCategory: urlCategory || undefined });
      } else if (pathname === '/how-it-works' || viewParam === 'how_it_works') {
        navigateTo('how_it_works');
      } else if (pathname === '/become-a-professional' || viewParam === 'become_pro') {
        navigateTo('become_pro');
      } else if (pathname === '/for-customers' || pathname === '/customer-guide' || viewParam === 'customer_guide') {
        navigateTo('customer_guide');
      } else if (pathname === '/verification' || pathname === '/trust' || viewParam === 'verification') {
        navigateTo('verification');
      } else if (pathname === '/pricing' || viewParam === 'pricing') {
        navigateTo('become_pro');
      } else if (pathname === '/faq' || viewParam === 'faq') {
        navigateTo('faq');
      } else if (pathname === '/about' || viewParam === 'about') {
        navigateTo('about');
      } else if (pathname === '/contact' || viewParam === 'contact') {
        navigateTo('contact');
      } else if (pathname === '/privacy' || viewParam === 'privacy') {
        navigateTo('privacy');
      } else if (pathname === '/terms' || viewParam === 'terms') {
        navigateTo('terms');
      } else if (
        typeof window !== 'undefined' &&
        window.sessionStorage?.getItem('jobmatch_post_job_active') === 'true' &&
        (pathname === '' || pathname === '/') &&
        !viewParam
      ) {
        setCurrentView('post_job');
      } else if (pathname === '' || pathname === '/' || viewParam === 'home') {
        setCurrentView('home');
      } else if (pathname && pathname !== '/') {
        setCurrentView('not_found');
      }
    };

    handleUrlRouting();
    window.addEventListener('popstate', handleUrlRouting);

    const handleServiceWorkerPushClick = (event: MessageEvent) => {
      const data = event?.data;
      if (data && data.type === 'JOBMATCH_PUSH_NOTIFICATION_CLICK' && typeof data.url === 'string') {
        try {
          const parsed = new URL(data.url, window.location.origin);
          window.history.pushState({}, '', `${parsed.pathname}${parsed.search}${parsed.hash}`);
          handleUrlRouting();
        } catch {
          // Ignore malformed URL
        }
      }
    };

    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerPushClick);
    }

    return () => {
      window.removeEventListener('popstate', handleUrlRouting);
      if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerPushClick);
      }
    };
  }, []);

  // Sync subscription status when logged-in user is a professional
  useEffect(() => {
    if (currentUser?.role === 'professional') {
      refreshSubscriptionStatus(currentUser.id);
    }
  }, [currentUser?.id, currentUser?.role, refreshSubscriptionStatus]);

  const switchUserRole = (role: UserRole) => {
    if (role === 'admin') {
      if (currentUser?.role === 'admin') {
        navigateTo('admin_dashboard');
      } else {
        navigateTo('admin_auth');
      }
      return;
    }

    const match = profiles.find((p) => p.role === role);
    if (match) {
      setCurrentUser(match);
      safeSetStorage('jobmatch_current_user', match);
      if (role === 'customer') navigateTo('customer_dashboard');
      else if (role === 'professional') navigateTo('pro_dashboard');
    }
  };

  // Initiate Google OAuth Sign-In / Registration using existing Supabase Authentication client
  const initiateGoogleSignIn = useCallback(
    async (options: {
      role?: 'customer' | 'professional';
      authMode: 'login' | 'register';
      returnView?: string;
    }) => {
      setGoogleAuthNotice(null);
      await signInWithGoogleOAuth({
        role: options.role,
        authMode: options.authMode,
        returnView: options.returnView || viewParams.returnView,
      });
    },
    [viewParams.returnView]
  );

  // Allow user to explicitly select Customer or Professional role after Google authentication (never auto-assigned)
  const selectRoleForGoogleUser = useCallback(
    (role: 'customer' | 'professional') => {
      setPendingGoogleAuth((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          status: 'requires_onboarding',
          requestedRole: role,
          authMode: 'register',
          message:
            role === 'professional'
              ? 'Profile completion required & Phone verification required. Please complete your professional application, verification documents, and SMS OTP.'
              : 'Profile completion required & Phone verification required. Please complete your customer details and verify your Nigerian phone number via SMS OTP.',
        };
      });
      saveGoogleOAuthIntent({ role, authMode: 'register' });
      setAuthModalState((prev) => ({ ...prev, isOpen: false }));
      if (role === 'professional') {
        setCurrentView('pro_auth');
        setViewParams((prev) => ({ ...prev, authMode: 'register' }));
      } else {
        setCurrentView('customer_auth');
        setViewParams((prev) => ({ ...prev, authMode: 'register' }));
      }
    },
    []
  );

  // Allow authenticated users with dual roles to switch active dashboard and view seamlessly
  const switchActiveRole = useCallback(
    (targetRole: 'customer' | 'professional') => {
      if (!currentUser) return;
      if (currentUser.role === targetRole) return;
      const available = currentUser.availableRoles || [currentUser.role];
      const canSwitch =
        available.includes(targetRole) ||
        (targetRole === 'customer' && currentUser.role === 'professional') ||
        (targetRole === 'professional' && currentUser.role === 'customer' && currentUser.verificationStatus === 'verified');

      if (!canSwitch) {
        console.warn('Role switch not permitted without role access:', targetRole);
        return;
      }

      const updatedUser: Profile = {
        ...currentUser,
        role: targetRole,
        activeRole: targetRole,
      };
      setCurrentUser(updatedUser);
      safeSetStorage('jobmatch_current_user', updatedUser);
      setCurrentView(targetRole === 'professional' ? 'pro_dashboard' : 'customer_dashboard');
    },
    [currentUser]
  );

  // Register Customer with Supabase Auth
  const registerCustomer = async (
    data: Partial<Profile> & { googleUserId?: string },
    password?: string
  ): Promise<Profile> => {
    const regResult = await signUpCustomerAccount({
      email: data.email || 'customer@example.com',
      password,
      fullName: data.fullName || 'New Customer',
      phone: cleanContactPhone(data.phone),
      state: data.state || 'Lagos',
      lga: data.lga || 'Ikeja',
      address: data.address,
      avatarUrl: data.avatarUrl,
      registrationSessionId: data.registrationSessionId,
      verificationProofToken: data.verificationProofToken,
      googleUserId: data.googleUserId || pendingGoogleAuth?.googleUser?.id,
    });

    const newCustomer = regResult.profile;
    setPendingGoogleAuth(null);
    clearStoredGoogleOAuthIntent();
    setProfiles((prev) => [newCustomer, ...prev.filter((p) => p.id !== newCustomer.id)]);
    setCurrentUser(newCustomer);
    safeSetStorage('jobmatch_current_user', newCustomer);

    if (!regResult.requiresEmailConfirmation) {
      const target = viewParams.returnView || 'customer_dashboard';
      navigateTo(target);
    }
    return { ...newCustomer, requiresEmailConfirmation: regResult.requiresEmailConfirmation } as any;
  };

  // Upgrade Existing Customer to Professional
  const upgradeCustomerToProfessional = async (data: Partial<Profile>): Promise<Profile> => {
    if (!currentUser) throw new Error('You must be signed in to upgrade your account.');

    const proProfile = await upgradeCustomerToProfessionalAccount(currentUser.id, {
      fullName: data.fullName || currentUser.fullName,
      businessName: data.businessName || `${data.fullName || currentUser.fullName}'s Technical Services`,
      email: currentUser.email,
      phone: data.phone || currentUser.phone || '',
      primaryCategory: data.primaryCategory || 'plumber',
      secondaryCategories: data.secondaryCategories || [],
      serviceAreas: data.serviceAreas || [data.lga || currentUser.lga || 'Ikeja'],
      yearsExperience: data.yearsExperience || 3,
      hourlyRate: data.hourlyRate || 8000,
      idNumber: data.idNumber,
      state: data.state || currentUser.state || 'Lagos',
      lga: data.lga || currentUser.lga || 'Ikeja',
      address: data.address || currentUser.address || '',
      bio: data.bio,
    });

    setProfiles((prev) => [proProfile, ...prev.filter((p) => p.id !== proProfile.id)]);
    setCurrentUser(proProfile);
    safeSetStorage('jobmatch_current_user', proProfile);

    // Notify Admin of upgrade
    const notif: Notification = {
      id: `notif-${Date.now()}`,
      userId: 'admin-1',
      title: 'Artisan Account Upgraded',
      message: `${proProfile.fullName} upgraded their account to an artisan account (${proProfile.primaryCategory}).`,
      type: 'verification',
      targetView: 'admin_dashboard',
      read: false,
      createdAt: new Date().toISOString(),
    };
    setNotifications((prev) => [notif, ...prev]);

    const target = viewParams.returnView || 'pro_dashboard';
    navigateTo(target);
    return proProfile;
  };

  // Register Professional with Supabase Auth & Verifications
  const registerProfessional = async (data: Partial<Profile>, password?: string): Promise<Profile> => {
    // If the user is currently signed in as a customer, or has an existing account with the same email, upgrade them directly!
    if (
      currentUser &&
      (currentUser.role === 'customer' ||
        (data.email && currentUser.email.toLowerCase() === data.email.trim().toLowerCase()))
    ) {
      return await upgradeCustomerToProfessional(data);
    }

    const regResult = await signUpProfessionalAccount({
      email: data.email || 'pro@jobmatch.ng',
      password,
      fullName: data.fullName || 'Skilled Artisan',
      businessName: data.businessName || `${data.fullName}'s Technical Services`,
      phone: cleanContactPhone(data.phone),
      primaryCategory: data.primaryCategory || 'plumber',
      secondaryCategories: data.secondaryCategories || [],
      serviceAreas: data.serviceAreas || [data.lga || 'Ikeja'],
      isAvailable: data.isAvailable ?? true,
      yearsExperience: data.yearsExperience || 3,
      hourlyRate: data.hourlyRate || 8000,
      idNumber: data.idNumber || 'NIN-1002938481',
      state: data.state || 'Lagos',
      lga: data.lga || 'Ikeja',
      bio: data.bio,
      avatarUrl: data.avatarUrl,
    });

    const newPro = regResult.profile;
    setProfiles((prev) => [newPro, ...prev]);
    setCurrentUser(newPro);
    safeSetStorage('jobmatch_current_user', newPro);

    // Notify Admin of new registration
    const notif: Notification = {
      id: `notif-${Date.now()}`,
      userId: 'admin-1',
      title: 'New Artisan Registration',
      message: `${newPro.fullName} registered for ${newPro.primaryCategory}. Awaiting NIN document verification.`,
      type: 'verification',
      targetView: 'admin_dashboard',
      read: false,
      createdAt: new Date().toISOString(),
    };
    setNotifications((prev) => [notif, ...prev]);

    if (!regResult.requiresEmailConfirmation) {
      const target = viewParams.returnView || 'pro_dashboard';
      navigateTo(target);
    }
    return { ...newPro, requiresEmailConfirmation: regResult.requiresEmailConfirmation } as any;
  };

  // Update Customer Profile
  const updateCustomerProfile = async (data: Partial<Profile>): Promise<Profile> => {
    if (!currentUser) throw new Error('You must be signed in to update your profile.');

    const updated: Profile = {
      ...currentUser,
      ...data,
      fullName: data.fullName !== undefined ? data.fullName : currentUser.fullName,
      phone: data.phone !== undefined ? data.phone : currentUser.phone,
      state: data.state !== undefined ? data.state : currentUser.state,
      lga: data.lga !== undefined ? data.lga : currentUser.lga,
      cityArea: data.cityArea !== undefined ? data.cityArea : currentUser.cityArea,
      address: data.address !== undefined ? data.address : currentUser.address,
      avatarUrl: data.avatarUrl !== undefined ? data.avatarUrl : currentUser.avatarUrl,
    };

    let score = 0;
    if (updated.fullName) score += 20;
    if (updated.email) score += 20;
    if (updated.phone) score += 20;
    if (updated.state && updated.lga) score += 20;
    if (updated.address) score += 20;
    updated.profileCompletionPercentage = score;
    updated.profileCompleted = score === 100;

    setCurrentUser(updated);
    safeSetStorage('jobmatch_current_user', updated);
    setProfiles((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));

    try {
      await fetch(`/api/profiles/customer/${updated.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': updated.id,
          'x-user-role': 'customer',
        },
        body: JSON.stringify(data),
      });
      const changedFields: string[] = [];
      if (data.fullName && data.fullName !== currentUser.fullName) changedFields.push('Full Name');
      if (data.phone && data.phone !== currentUser.phone) changedFields.push('Phone Number');
      if ((data.state && data.state !== currentUser.state) || (data.lga && data.lga !== currentUser.lga) || (data.address && data.address !== currentUser.address)) changedFields.push('Service Location / Address');
      if (changedFields.length > 0) {
        notifyProfileUpdatedApi(updated.id, changedFields).catch(() => {});
      }
    } catch (err) {
      console.warn('API updateCustomerProfile notice:', err);
    }

    return updated;
  };

  // Update Professional Profile
  const updateProfessionalProfile = async (data: Partial<Profile>): Promise<Profile> => {
    if (!currentUser) throw new Error('You must be signed in to update your profile.');

    const updated: Profile = {
      ...currentUser,
      ...data,
      fullName: data.fullName !== undefined ? data.fullName : currentUser.fullName,
      businessName: data.businessName !== undefined ? data.businessName : currentUser.businessName,
      phone: data.phone !== undefined ? data.phone : currentUser.phone,
      primaryCategory: data.primaryCategory !== undefined ? data.primaryCategory : currentUser.primaryCategory,
      secondaryCategories: data.secondaryCategories !== undefined ? data.secondaryCategories : currentUser.secondaryCategories,
      skills: data.skills !== undefined ? data.skills : currentUser.skills,
      yearsExperience: data.yearsExperience !== undefined ? data.yearsExperience : currentUser.yearsExperience,
      hourlyRate: data.hourlyRate !== undefined ? data.hourlyRate : currentUser.hourlyRate,
      state: data.state !== undefined ? data.state : currentUser.state,
      lga: data.lga !== undefined ? data.lga : currentUser.lga,
      cityArea: data.cityArea !== undefined ? data.cityArea : currentUser.cityArea,
      address: data.address !== undefined ? data.address : currentUser.address,
      serviceAreas: data.serviceAreas !== undefined ? data.serviceAreas : currentUser.serviceAreas,
      bio: data.bio !== undefined ? data.bio : currentUser.bio,
      avatarUrl: data.avatarUrl !== undefined ? data.avatarUrl : currentUser.avatarUrl,
      isAvailable: data.isAvailable !== undefined ? data.isAvailable : currentUser.isAvailable,
    };

    let score = 0;
    if (updated.fullName) score += 10;
    if (updated.businessName) score += 10;
    if (updated.email) score += 10;
    if (updated.phone) score += 10;
    if (updated.primaryCategory) score += 10;
    if (updated.yearsExperience && updated.yearsExperience > 0) score += 10;
    if (updated.hourlyRate && updated.hourlyRate > 0) score += 10;
    if (updated.state && updated.lga) score += 10;
    if (updated.serviceAreas && updated.serviceAreas.length > 0) score += 10;
    if (updated.bio && updated.bio.length > 10) score += 10;
    updated.profileCompletionPercentage = score;
    updated.profileCompleted = score >= 90;

    setCurrentUser(updated);
    safeSetStorage('jobmatch_current_user', updated);
    setProfiles((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));

    try {
      await fetch(`/api/profiles/professional/${updated.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': updated.id,
          'x-user-role': 'professional',
        },
        body: JSON.stringify(data),
      });

      // Sync services and subcategories to Stage 4 professional services API
      if (updated.primaryCategory) {
        await fetch(`/api/professionals/${updated.id}/services`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'x-user-id': updated.id,
            'x-user-role': 'professional',
          },
          body: JSON.stringify({
            primaryCategory: updated.primaryCategory,
            additionalCategories: updated.secondaryCategories || [],
            subcategories: updated.specialties || [],
          }),
        });
      }
      const changedFields: string[] = [];
      if (data.fullName && data.fullName !== currentUser.fullName) changedFields.push('Full Name');
      if (data.businessName && data.businessName !== currentUser.businessName) changedFields.push('Business Name');
      if (data.phone && data.phone !== currentUser.phone) changedFields.push('Phone Number');
      if (data.primaryCategory && data.primaryCategory !== currentUser.primaryCategory) changedFields.push('Primary Service Category');
      if ((data.state && data.state !== currentUser.state) || (data.lga && data.lga !== currentUser.lga) || (data.serviceAreas && JSON.stringify(data.serviceAreas) !== JSON.stringify(currentUser.serviceAreas))) changedFields.push('Service Areas & Location');
      if (changedFields.length > 0) {
        notifyProfileUpdatedApi(updated.id, changedFields).catch(() => {});
      }
    } catch (err) {
      console.warn('API updateProfessionalProfile notice:', err);
    }

    return updated;
  };

  // Sign In with Supabase Auth & Authoritative Profile Verification
  const loginUser = async (
    emailOrPhone: string,
    role: UserRole,
    password?: string,
    verified2faResult?: any
  ): Promise<boolean> => {
    let cleanEmail = emailOrPhone.trim().toLowerCase();

    if (!cleanEmail) {
      throw new Error('Please enter your email address or phone number.');
    }

    // Support phone number as login identifier
    if (!cleanEmail.includes('@')) {
      const numericPhone = cleanEmail.replace(/\D/g, '');
      const match = profiles.find((p) => {
        if (!p.phone) return false;
        const pNum = p.phone.replace(/\D/g, '');
        return pNum === numericPhone || pNum.endsWith(numericPhone) || numericPhone.endsWith(pNum);
      });
      if (match?.email) {
        cleanEmail = match.email.toLowerCase();
      } else if (verified2faResult?.user?.email) {
        cleanEmail = String(verified2faResult.user.email).toLowerCase();
      }
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(cleanEmail)) {
      throw new Error('Invalid email or password');
    }
    if (!password && !verified2faResult?.twoFactorVerified) {
      throw new Error('Please enter your password.');
    }

    // 1. Strict Administrator Authentication Flow via Supabase
    if (role === 'admin' || cleanEmail === 'admin@jobmatch.ng') {
      const res = await signInAdmin(cleanEmail, password || '');
      if (res.profile) {
        setCurrentUser(res.profile);
        safeSetStorage('jobmatch_current_user', res.profile);
        setProfiles((prev) => {
          const exists = prev.some(
            (p) => p.id === res.profile.id || p.email.toLowerCase() === res.profile.email.toLowerCase()
          );
          if (exists) {
            return prev.map((p) =>
              p.id === res.profile.id || p.email.toLowerCase() === res.profile.email.toLowerCase() ? res.profile : p
            );
          }
          return [...prev, res.profile];
        });
        const targetView = viewParams.returnView || 'admin_dashboard';
        navigateTo(targetView);
        return true;
      }
      throw new Error('Invalid email or password');
    }

    // 2. Customer and Professional Direct Authentication (No OTP required for normal login)
    let authResult = verified2faResult;
    if (!authResult || !authResult.session?.access_token) {
      if (!password) {
        throw new Error('Please enter your password.');
      }
      const isEmail = cleanEmail.includes('@');
      const apiRes = await sendTwoFactorCodeApi({
        email: isEmail ? cleanEmail : undefined,
        phone: !isEmail ? cleanEmail : undefined,
        password,
        userType: role === 'professional' ? 'professional' : 'customer',
        purpose: 'login_2fa',
      });
      if (!apiRes.success) {
        throw new Error(apiRes.message || 'Invalid email or password');
      }
      authResult = apiRes;
    }

    if (authResult?.twoFactorSessionToken) {
      safeSetStorage('jobmatch_2fa_verified_token', authResult.twoFactorSessionToken);
    }
    setPendingGoogleAuth(null);
    clearStoredGoogleOAuthIntent();

    let authUser: any = authResult?.user || null;
    if (authResult?.session?.access_token && authResult?.session?.refresh_token) {
      const { data: sessionData, error: sessionErr } = await supabase.auth.setSession({
        access_token: authResult.session.access_token,
        refresh_token: authResult.session.refresh_token,
      });

      if (sessionData?.user) {
        authUser = sessionData.user;
      } else if (sessionErr && !authUser) {
        const userFriendlyMessage = getSupabaseAuthErrorMessage(sessionErr);
        throw new Error(userFriendlyMessage);
      }
    }

    if (!authUser && cleanEmail.includes('@') && password) {
      const { data: directSign, error: directErr } = await supabase.auth.signInWithPassword({
        email: cleanEmail,
        password,
      });
      if (directErr) {
        throw new Error(getSupabaseAuthErrorMessage(directErr));
      }
      if (directSign?.user) {
        authUser = directSign.user;
      }
    }

    if (!authUser) {
      throw new Error('Invalid email or password');
    }

    const authData = { user: authUser };

    // 3. Fetch User Profile
    const userProfile =
      (await fetchProfileForAuthUser(
        authData.user.id,
        authData.user.email || cleanEmail,
        profiles
      )) || (verified2faResult.profile as Profile | null);

    const userActualRole: UserRole = userProfile
      ? userProfile.role
      : ((authData.user.user_metadata?.role as UserRole) === 'professional' ? 'professional' : 'customer');

    // Strict role validation: refuse cross-role login between Customer and Professional dashboards
    if (role === 'customer' && userActualRole === 'professional') {
      await supabase.auth.signOut().catch(() => {});
      setCurrentUser(null);
      safeSetStorage('jobmatch_current_user', null);
      throw new Error(
        'Access Refused: This sign-in is for Customer accounts only. Your account is registered as a Professional. Please go to the Professional Login page to access your dashboard.'
      );
    }

    if (role === 'professional' && userActualRole === 'customer') {
      await supabase.auth.signOut().catch(() => {});
      setCurrentUser(null);
      safeSetStorage('jobmatch_current_user', null);
      throw new Error(
        'Access Refused: This sign-in is for Professional accounts only. Your account is registered as a Customer. Please go to the Customer Login page to access your dashboard.'
      );
    }

    if (!userProfile) {
      // Create minimal profile matching authenticated user
      const meta = authData.user.user_metadata || {};
      const userRole: UserRole = meta.role === 'professional' ? 'professional' : 'customer';
      const newProfile: Profile = {
        id: authData.user.id,
        role: userRole,
        fullName: meta.full_name || authData.user.email?.split('@')[0] || 'User',
        email: authData.user.email || cleanEmail,
        phone: meta.phone || '',
        avatarUrl:
          meta.avatar_url ||
          (userRole === 'professional'
            ? 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&auto=format&fit=crop&q=80'
            : 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=200&auto=format&fit=crop&q=80'),
        state: meta.state || 'Lagos',
        lga: meta.lga || 'Ikeja',
        address: meta.address || '',
        createdAt: authData.user.created_at || new Date().toISOString(),
      };
      setCurrentUser(newProfile);
      safeSetStorage('jobmatch_current_user', newProfile);
      setProfiles((prev) => [newProfile, ...prev]);

      // Ensure profile is recorded in Supabase database
      try {
        await supabase.from('profiles').upsert({
          id: authData.user.id,
          role: userRole,
          full_name: newProfile.fullName,
          email: cleanEmail,
          phone: newProfile.phone,
          state: newProfile.state,
          lga: newProfile.lga,
          address: newProfile.address,
          avatar_url: newProfile.avatarUrl,
          updated_at: new Date().toISOString(),
        });
        if (userRole === 'customer') {
          await supabase.from('customer_profiles').upsert({ id: authData.user.id });
        } else if (userRole === 'professional') {
          const { data: existingPro } = await supabase
            .from('professional_profiles')
            .select('primary_category, business_name')
            .eq('id', authData.user.id)
            .maybeSingle();
          const resolvedCategory =
            existingPro?.primary_category ||
            meta.primary_category ||
            meta.trade_category ||
            newProfile.primaryCategory ||
            'plumber';
          await supabase.from('professional_profiles').upsert({
            id: authData.user.id,
            primary_category: resolvedCategory,
            business_name:
              existingPro?.business_name ||
              meta.business_name ||
              `${newProfile.fullName}'s Technical Services`,
          });
        }
      } catch (dbErr) {
        console.warn('Persist missing profile to Supabase notice:', dbErr);
      }

      const target =
        viewParams.returnView ||
        (newProfile.role === 'professional' ? 'pro_dashboard' : 'customer_dashboard');
      navigateTo(target);
      return true;
    }

    if (userProfile.isSuspended) {
      await supabase.auth.signOut().catch(() => {});
      setCurrentUser(null);
      safeSetStorage('jobmatch_current_user', null);
      throw new Error('Your account is currently suspended. Please contact support.');
    }

    setCurrentUser(userProfile);
    safeSetStorage('jobmatch_current_user', userProfile);
    notifyLoginActivityApi(userProfile.id).catch(() => {});

    // Determine target view: honor returnView if valid for this user's role
    let targetView: AppView = userProfile.role === 'professional' ? 'pro_dashboard' : 'customer_dashboard';
    if (viewParams.returnView) {
      if (
        userProfile.role === 'customer' &&
        (viewParams.returnView === 'post_job' ||
          viewParams.returnView === 'customer_dashboard' ||
          viewParams.returnView === 'messages' ||
          viewParams.returnView === 'notifications' ||
          viewParams.returnView === 'quote_comparison')
      ) {
        targetView = viewParams.returnView;
      } else if (
        userProfile.role === 'professional' &&
        (viewParams.returnView === 'available_jobs' ||
          viewParams.returnView === 'pro_dashboard' ||
          viewParams.returnView === 'pro_profile' ||
          viewParams.returnView === 'pro_subscription' ||
          viewParams.returnView === 'messages' ||
          viewParams.returnView === 'notifications')
      ) {
        targetView = viewParams.returnView;
      }
    }

    navigateTo(targetView);
    return true;
  };

  // Genuine Complete Logout
  const logoutUser = async (): Promise<void> => {
    try {
      await supabase.auth.signOut();
    } catch (_) {}
    safeSetStorage('jobmatch_current_user', null);
    safeSetStorage('jobmatch_admin_session', null);
    safeSetStorage('jobmatch_2fa_verified_token', null);
    safeSetStorage('jobmatch_pending_google_auth', null);
    clearStoredGoogleOAuthIntent();
    try {
      localStorage.removeItem('jobmatch_current_user');
      localStorage.removeItem('jobmatch_admin_session');
      localStorage.removeItem('jobmatch_2fa_verified_token');
      localStorage.removeItem('jobmatch_pending_google_auth');
    } catch (_) {}
    setPendingGoogleAuth(null);
    setGoogleAuthNotice(null);
    setCurrentUser(null);
    navigateTo('home');
  };

  // Secure Admin Session Termination
  const adminLogout = async (): Promise<void> => {
    try {
      await signOutAdmin();
    } catch (_) {}
    safeSetStorage('jobmatch_current_user', null);
    safeSetStorage('jobmatch_admin_session', null);
    try {
      localStorage.removeItem('jobmatch_current_user');
      localStorage.removeItem('jobmatch_admin_session');
    } catch (_) {}
    setCurrentUser(null);
    navigateTo('admin_auth');
  };

  // Post Job with Supabase Storage upload & Jobs table insert
  const postJob = async (
    jobData: Omit<Job, 'id' | 'createdAt' | 'updatedAt' | 'quotesCount' | 'status'>,
    mediaFiles?: File[],
    videoFiles?: File[]
  ): Promise<Job> => {
    if (!currentUser) {
      throw new Error('Please sign in to post a job.');
    }

    const newJob = await createSupabaseJob(
      {
        ...jobData,
        customerId: currentUser.id,
        customerName: currentUser.fullName,
        customerPhone: currentUser.phone,
        customerAvatar: currentUser.avatarUrl,
      },
      mediaFiles,
      videoFiles
    );

    setJobs((prev) => [newJob, ...prev]);

    // Algorithmic Matching Engine: Matches professionals & inserts into job_matches table
    try {
      const matchedResults = await runJobMatchingEngine(newJob, profiles);
      if (matchedResults.length > 0) {
        const directTargetId = getAssignedProfessionalId(newJob);
        const matchingNotifs: Notification[] = matchedResults
          .filter((res) =>
            directTargetId
              ? res.professional.id === directTargetId
              : isJobForProfessionalTrade(newJob, res.professional)
          )
          .map((res) => ({
            id: `notif-${Date.now()}-${res.professional.id}`,
            userId: res.professional.id,
            title: directTargetId
              ? `Direct Job Request from ${newJob.customerName}!`
              : `New Job Match: ${newJob.categoryName} (${res.matchScore}% Match)`,
            message: directTargetId
              ? `${newJob.customerName} selected you directly for "${newJob.title}" in ${newJob.lga}, ${newJob.state}. Target budget: ₦${(newJob.budgetEstimate || 0).toLocaleString()}`
              : `${newJob.title} in ${newJob.lga}, ${newJob.state}. Target budget: ₦${(newJob.budgetEstimate || 0).toLocaleString()}`,
            type: 'job_status',
            targetView: directTargetId ? 'pro_dashboard' : 'available_jobs',
            targetJobId: newJob.id,
            read: false,
            createdAt: new Date().toISOString(),
          }));
        setNotifications((prev) => [...matchingNotifs, ...prev]);
      }
    } catch (err) {
      console.warn('Matching engine notice:', err);
    }

    navigateTo('customer_dashboard');
    return newJob;
  };

  // Submit Quote with Quotes table insert and verified professional access enforcement (No subscription fee required)
  const submitQuote = async (quoteData: Omit<Quote, 'id' | 'createdAt' | 'status'>): Promise<Quote> => {
    if (!currentUser) {
      throw new Error('Please sign in as a verified professional to submit a quote.');
    }

    // Access control: User must be a professional
    if (currentUser.role !== 'professional') {
      throw new Error('Only registered professional artisan accounts can submit quotes.');
    }

    // Access control: Professional must be verified/approved by administrator
    const isVerified =
      currentUser.verificationStatus === 'verified' ||
      (currentUser as any).isVerified === true ||
      Boolean((currentUser as any).is_verified);

    if (!isVerified) {
      throw new Error(
        'Identity & Safety Verification Required: Your professional account must be verified by an administrator before you can submit quotes. Please complete verification on your dashboard.'
      );
    }

    // Identity security: Professional can only submit quotes for their own authenticated account
    if (quoteData.professionalId && quoteData.professionalId !== currentUser.id) {
      throw new Error('Unauthorized: You can only submit quotes using your own authenticated professional account.');
    }

    // Trade & Direct Assignment Validation: Ensure professional is qualified for this job's trade and not blocked by another artisan's assignment
    const targetJobForValidation = jobs.find((j) => j.id === quoteData.jobId);
    if (targetJobForValidation) {
      const assignedProId = getAssignedProfessionalId(targetJobForValidation);
      if (assignedProId && assignedProId !== currentUser.id) {
        throw new Error('This job has already been assigned directly to another professional.');
      }
      if (!isJobForProfessionalTrade(targetJobForValidation, currentUser)) {
        throw new Error(
          `Trade Mismatch: This job requires a verified ${targetJobForValidation.categoryName || targetJobForValidation.categorySlug}, which does not match your registered trade (${currentUser.primaryCategory}).`
        );
      }
    }

    // Prevent duplicate quote submissions for the same job by the same professional
    const existingQuote = quotes.find(
      (q) => q.jobId === quoteData.jobId && q.professionalId === currentUser.id
    );
    if (existingQuote) {
      throw new Error('Quote has already been sent for this job.');
    }

    const newQuote = await submitSupabaseQuote({
      ...quoteData,
      professionalId: currentUser.id,
      professionalName: currentUser.fullName,
      professionalAvatar: currentUser.avatarUrl,
      professionalBusiness: currentUser.businessName || `${currentUser.fullName} Services`,
      professionalCategory: currentUser.primaryCategory || quoteData.professionalCategory,
    });

    setQuotes((prev) => [newQuote, ...prev]);

    // Update job quote count
    setJobs((prev) =>
      prev.map((j) => (j.id === quoteData.jobId ? { ...j, quotesCount: (j.quotesCount || 0) + 1 } : j))
    );

    // Notify customer
    const targetJob = jobs.find((j) => j.id === quoteData.jobId);
    if (targetJob) {
      const notif: Notification = {
        id: `notif-${Date.now()}`,
        userId: targetJob.customerId,
        title: `New Quote Received! ₦${quoteData.totalAmount.toLocaleString()}`,
        message: `${quoteData.professionalName} submitted an itemized quote for: ${targetJob.title}`,
        type: 'quote_received',
        targetView: 'quote_comparison',
        targetJobId: targetJob.id,
        read: false,
        createdAt: new Date().toISOString(),
      };
      setNotifications((prev) => [notif, ...prev]);

      // Notify customer via WhatsApp if job was created or linked via WhatsApp
      try {
        fetch('/api/quotes/notify-whatsapp', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jobId: targetJob.id,
            quote: {
              professionalName: quoteData.professionalName,
              professionalRating: quoteData.professionalRating || 5.0,
              totalAmount: quoteData.totalAmount,
              durationEstimate: quoteData.durationEstimate,
            },
          }),
        }).catch((e) => console.info('WhatsApp quote notification fetch note:', e));
      } catch (_) {}
    }

    navigateTo('pro_dashboard');
    return newQuote;
  };

  // Accept Quote with live database updates
  const selectProfessionalAndAcceptQuote = async (
    quoteId: string,
    options?: { skipNavigate?: boolean; alreadyPaid?: boolean; paymentReference?: string }
  ) => {
    if (!currentUser) return;
    const selectedQuote = quotes.find((q) => q.id === quoteId);
    if (!selectedQuote) return;

    const currentJobObj = jobs.find((j) => j.id === selectedQuote.jobId);
    const isAlreadyPaid = Boolean(
      options?.alreadyPaid ||
        currentJobObj?.status === 'in_progress' ||
        currentJobObj?.paymentStatus === 'held_in_escrow' ||
        (currentJobObj as any)?.payment_status === 'held_in_escrow' ||
        currentJobObj?.escrow_held
    );

    // Call Supabase update
    await acceptSupabaseQuote(
      quoteId,
      selectedQuote.jobId,
      selectedQuote.professionalId,
      currentUser.id,
      { alreadyPaid: isAlreadyPaid, paymentReference: options?.paymentReference }
    );

    // 1. Update quote status to accepted, other quotes to declined
    setQuotes((prev) =>
      prev.map((q) => {
        if (q.jobId === selectedQuote.jobId) {
          return q.id === quoteId ? { ...q, status: 'accepted' } : { ...q, status: 'declined' };
        }
        return q;
      })
    );

    // 2. Update job status (keep in_progress & held_in_escrow if already paid)
    setJobs((prev) =>
      prev.map((j) =>
        j.id === selectedQuote.jobId
          ? {
              ...j,
              status: isAlreadyPaid ? 'in_progress' : 'assigned',
              paymentStatus: (isAlreadyPaid ? 'held_in_escrow' : 'awaiting_payment') as any,
              payment_status: (isAlreadyPaid ? 'held_in_escrow' : 'awaiting_payment') as any,
              escrow_held: isAlreadyPaid ? true : j.escrow_held,
              payment_reference: options?.paymentReference || j.payment_reference,
              selectedProfessionalId: selectedQuote.professionalId,
              acceptedQuoteId: selectedQuote.id,
              grossAmount: selectedQuote.totalAmount,
              updatedAt: new Date().toISOString(),
            }
          : j
      )
    );

    if (!isAlreadyPaid) {
      // 3. Notify artisan (instructing not to start work until escrow payment is confirmed)
      const notif: Notification = {
        id: `notif-${Date.now()}`,
        userId: selectedQuote.professionalId,
        title: 'Quote Accepted — Awaiting Customer Escrow Payment ⏳',
        message: `Your quote for ₦${selectedQuote.totalAmount.toLocaleString()} was accepted. Wait for Paystack escrow payment confirmation before starting work.`,
        type: 'quote_accepted',
        targetView: 'pro_dashboard',
        targetJobId: selectedQuote.jobId,
        read: false,
        createdAt: new Date().toISOString(),
      };
      setNotifications((prev) => [notif, ...prev]);

      // Notify backend to send Telegram Paystack payment link to customer and alert professional
      try {
        fetch('/api/quotes/notify-acceptance-whatsapp', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jobId: selectedQuote.jobId,
            quoteId: selectedQuote.id,
            professionalId: selectedQuote.professionalId,
            quote: {
              id: selectedQuote.id,
              professionalName: selectedQuote.professionalName,
              totalAmount: selectedQuote.totalAmount,
            },
            customer: {
              id: currentUser.id,
              fullName: currentUser.fullName,
              phone: currentUser.phone,
              email: currentUser.email,
            },
          }),
        }).catch((e) => console.info('Quote acceptance notification note:', e));
      } catch (_) {}
    }

    if (!options?.skipNavigate) {
      navigateTo('customer_dashboard', isAlreadyPaid ? { tab: 'active', activeTab: 'active', jobId: selectedQuote.jobId } : undefined);
    }
  };

  // Send in-app message (Supports both job-linked and direct 1-to-1 conversations)
  const sendMessage = async (
    jobId: string | null | undefined,
    receiverId: string,
    content: string,
    attachments?: string[],
    conversationId?: string
  ) => {
    if (!currentUser) {
      throw new Error('Please sign in to send messages.');
    }

    const newMsg = await sendSupabaseMessage({
      jobId: jobId || null,
      conversationId,
      senderId: currentUser.id,
      receiverId,
      content,
      attachments,
      senderName: currentUser.fullName || (currentUser as any).businessName || 'You',
      senderRole: currentUser.role,
      senderAvatar: currentUser.avatarUrl,
    });

    setMessages((prev) => {
      const exists = prev.some((m) => m.id === newMsg.id);
      if (exists) {
        return prev.map((m) => (m.id === newMsg.id ? newMsg : m));
      }
      return [...prev, newMsg];
    });

    const preview =
      content.trim() ||
      (attachments && attachments.length > 0 ? '📎 Sent an attachment' : '');
    const notif: Notification = {
      id: `notif-${Date.now()}`,
      userId: receiverId,
      title: `New Message from ${currentUser.fullName}`,
      message: preview.length > 50 ? `${preview.substring(0, 50)}...` : preview,
      type: 'new_message',
      targetView: 'messages',
      targetJobId: jobId || undefined,
      linkUrl: jobId
        ? `/messages?jobId=${encodeURIComponent(jobId)}`
        : `/messages?contactId=${encodeURIComponent(currentUser.id)}`,
      read: false,
      createdAt: new Date().toISOString(),
    };
    setNotifications((prev) => [notif, ...prev]);
  };

  const fetchMessages = useCallback(
    async (
      jobId?: string | null,
      options?: { conversationId?: string; withUserId?: string }
    ): Promise<Message[]> => {
      try {
        const liveMsgs = await getSupabaseMessages(jobId, currentUser?.id, options);
        if (liveMsgs && liveMsgs.length > 0) {
          setMessages((prev) => {
            const map = new Map<string, Message>();
            prev.forEach((m) => map.set(m.id, m));
            liveMsgs.forEach((m) => map.set(m.id, m));
            return Array.from(map.values()).sort(
              (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
            );
          });
        }
        return liveMsgs;
      } catch (err) {
        console.warn('fetchMessages error:', err);
        return [];
      }
    },
    [currentUser?.id]
  );

  const markMessagesRead = useCallback(
    async (
      jobId: string | null | undefined,
      userId: string,
      options?: { conversationId?: string; senderId?: string }
    ) => {
      try {
        await markSupabaseMessagesRead(jobId, userId, options);
        const rawJobId = jobId ? String(jobId).trim() : '';
        const isDirect = !rawJobId || rawJobId.startsWith('direct:');
        const directSenderId = rawJobId.startsWith('direct:')
          ? rawJobId.replace('direct:', '').trim()
          : options?.senderId;

        setMessages((prev) =>
          prev.map((m) => {
            if (m.receiverId !== userId || m.read) return m;
            if (!isDirect && m.jobId === rawJobId) {
              return { ...m, read: true };
            }
            if (options?.conversationId && m.conversationId === options.conversationId) {
              return { ...m, read: true };
            }
            if (isDirect && directSenderId && !m.jobId && m.senderId === directSenderId) {
              return { ...m, read: true };
            }
            return m;
          })
        );
      } catch (_) {}
    },
    []
  );

  const markNotificationAsRead = (notificationId: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
    );
  };

  const markAllNotificationsAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const updateProfessionalVerification = (proId: string, status: VerificationStatus) => {
    setProfiles((prev) =>
      prev.map((p) => (p.id === proId ? { ...p, verificationStatus: status } : p))
    );

    supabase
      .from('professional_verifications')
      .update({ verification_status: status })
      .eq('professional_id', proId)
      .then(
        () => {},
        (err) => console.warn('Verification status update note:', err)
      );

    const targetPro = profiles.find((p) => p.id === proId);
    if (targetPro) {
      const notif: Notification = {
        id: `notif-${Date.now()}`,
        userId: proId,
        title: status === 'verified' ? 'NIN Verification Approved!' : 'Verification Update',
        message:
          status === 'verified'
            ? 'Your NIN and trade credentials have been verified by JobMatch Admin. You now display the Verified Pro badge.'
            : 'Your verification status has been updated.',
        type: 'verification',
        targetView: 'pro_dashboard',
        read: false,
        createdAt: new Date().toISOString(),
      };
      setNotifications((prev) => [notif, ...prev]);
    }
  };

  // FEATURE 1: Authoritative profile photo lock to biometric selfie
  const updateVerifiedProfilePhoto = async (proId: string, photoUrl: string): Promise<boolean> => {
    const nowIso = new Date().toISOString();
    setProfiles((prev) =>
      prev.map((p) =>
        p.id === proId
          ? {
              ...p,
              avatarUrl: photoUrl,
              isProfilePhotoVerified: true,
              verifiedProfilePhotoUrl: photoUrl,
              verifiedPhotoCapturedAt: nowIso,
            }
          : p
      )
    );

    if (currentUser?.id === proId) {
      setCurrentUser((prev) => (prev ? {
        ...prev,
        avatarUrl: photoUrl,
        isProfilePhotoVerified: true,
        verifiedProfilePhotoUrl: photoUrl,
        verifiedPhotoCapturedAt: nowIso,
      } : null));
    }

    return await updateVerifiedProfilePhotoDb(proId, photoUrl);
  };

  const unreadNotifsCount = notifications.filter(
    (n) =>
      !n.read &&
      (n.userId === currentUser?.id ||
        (currentUser?.role === 'admin' &&
          (n.userId === 'admin-1' || n.userId === 'e0e0ec57-cd3a-4f70-b980-c6b5f1ee7cd2')))
  ).length;

  return (
    <AppContext.Provider
      value={{
        currentView,
        viewParams,
        currentUser,
        isAuthenticated,
        authLoading,
        profiles,
        jobs,
        quotes,
        messages,
        notifications,
        reviews,
        unreadNotifsCount,
        supabaseHealth,
        showMigrationModal,
        setShowMigrationModal,
        checkHealth,
        navigateTo,
        authModalState,
        openAuthModal,
        closeAuthModal,
        setCurrentUser,
        switchUserRole,
        registerCustomer,
        registerProfessional,
        upgradeCustomerToProfessional,
        updateCustomerProfile,
        updateProfessionalProfile,
        loginUser,
        logoutUser,
        adminLogout,
        pendingGoogleAuth,
        setPendingGoogleAuth,
        googleAuthNotice,
        setGoogleAuthNotice,
        initiateGoogleSignIn,
        selectRoleForGoogleUser,
        switchActiveRole,
        postJob,
        submitQuote,
        selectProfessionalAndAcceptQuote,
        sendMessage,
        fetchMessages,
        markMessagesRead,
        onlineUserIds,
        markNotificationAsRead,
        markAllNotificationsAsRead,
        refreshSubscriptionStatus,
        refreshJobsAndQuotes,
        updateProfessionalVerification,
        updateVerifiedProfilePhoto,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export { useTheme, ThemeProvider } from './ThemeContext';
export type { Theme } from './ThemeContext';
